import { create } from 'zustand';
import { supabase } from '../supabase';
import { useSettingsStore } from './settingsStore';
import type { CalendarEvent, WorkSlot, CreateWorkSlotData, WorkSlotMetadata } from '../types';
import { ErrorCategory, parseErrorResponse } from '../hooks/useErrorHandler';
import { AlfaDocsService, type AlfaDocsConfig } from '../services/alfadocsService';
import { getAlfaDocsLinks, getPrimaryPracticeId, getPatientIdForPractice, parseAlfaSourceId, buildAlfaSourceId } from '../services/alfadocsLinks';
import { overlayGoogleEventColors } from '../utils/googleEventColorOverlay';
import { calendarSharingStore } from './calendarSharingStore';
import { presentSalesActivities } from '../calendar/presentActivities';

// Helper function to get a valid session, refreshing if necessary
const getValidSession = async () => {
  let { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session) {
    console.warn('Session invalid, attempting refresh...');
    const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      throw new Error('Not authenticated. Please log in again.');
    }

    session = refreshedSession;
    console.log('Session refreshed successfully');
  }

  return session;
};

/**
 * Best-effort: legge il body JSON dell'errore edge (FunctionsHttpError.context)
 * per il push Microsoft su calendario dell'assegnatario (F1B). Se la edge risponde
 * 401 con `target_user: true`, il messaggio deve indicare che è l'account del
 * COLLEGA (assegnatario) a dover essere riconnesso, non quello dell'utente corrente.
 * Ritorna un messaggio adeguato oppure il fallback.
 */
const resolveMicrosoftTargetErrorMessage = async (error: any, fallback: string): Promise<string> => {
  try {
    const ctx = error?.context;
    if (ctx && typeof ctx.clone === 'function') {
      const body = await ctx.clone().json().catch(() => null);
      if (body?.target_user === true) {
        return "L'account Microsoft del collega assegnatario dev'essere riconnesso: chiedigli di ricollegare il calendario dalle sue impostazioni.";
      }
      if (body?.error === 'forbidden') {
        return "Impossibile scrivere sul calendario Microsoft dell'assegnatario (account non collegato o org diversa).";
      }
      if (typeof body?.message === 'string') return body.message;
    }
  } catch {
    // ignora: usa fallback
  }
  return fallback;
};

// Helper function to map Google Calendar colorId to hex colors
const getGoogleEventColor = (colorId: string): string => {
  const colorMap: Record<string, string> = {
    '1': '#7986CB', // Lavender
    '2': '#33B679', // Sage
    '3': '#8E24AA', // Grape
    '4': '#E67C73', // Flamingo
    '5': '#F6BF26', // Banana
    '6': '#F4511E', // Tangerine
    '7': '#039BE5', // Peacock
    '8': '#0B8043', // Eucalyptus
    '9': '#3F51B5', // Blueberry
    '10': '#7CB342', // Basil
    '11': '#D50000', // Tomato
  };

  return colorMap[colorId] || '#039BE5'; // Default Google blue
};

// ============================================
// CALENDAR LIST CACHE (localStorage)
// Per mostrare i calendari immediatamente all'apertura della pagina
// ============================================

const CALENDAR_LIST_CACHE_KEY = 'google_calendar_list_cache';
const CALENDAR_LIST_CACHE_TIME_KEY = 'google_calendar_list_cache_time';
const CALENDAR_CACHE_TTL = 10 * 60 * 1000; // 10 minuti di TTL

interface CachedCalendar {
  id: string;
  summary: string;
  backgroundColor: string;
  primary: boolean;
}

interface CalendarListCache {
  userId: string;
  calendars: CachedCalendar[];
}

// Carica la lista calendari dalla cache (solo se appartiene all'utente corrente)
const loadCalendarListCache = (currentUserId: string): CachedCalendar[] | null => {
  try {
    const cached = localStorage.getItem(CALENDAR_LIST_CACHE_KEY);
    const cacheTime = localStorage.getItem(CALENDAR_LIST_CACHE_TIME_KEY);
    if (cached && cacheTime) {
      const timestamp = parseInt(cacheTime, 10);
      if (Date.now() - timestamp < CALENDAR_CACHE_TTL) {
        const cacheData = JSON.parse(cached);

        // Verifica che la cache appartenga all'utente corrente
        if (cacheData.userId && cacheData.userId === currentUserId) {
          console.log('[CalendarCache] Loaded calendar list from cache for user:', currentUserId);
          return cacheData.calendars;
        } else {
          console.log('[CalendarCache] Cache belongs to different user, ignoring');
          return null;
        }
      } else {
        console.log('[CalendarCache] Cache expired, will refresh');
      }
    }
  } catch (e) {
    console.error('[CalendarCache] Error loading calendar cache:', e);
  }
  return null;
};

// Salva la lista calendari nella cache con l'userId
const saveCalendarListCache = (calendars: CachedCalendar[], userId: string) => {
  try {
    const cacheData: CalendarListCache = {
      userId,
      calendars
    };
    localStorage.setItem(CALENDAR_LIST_CACHE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(CALENDAR_LIST_CACHE_TIME_KEY, Date.now().toString());
    console.log('[CalendarCache] Saved', calendars.length, 'calendars to cache for user:', userId);
  } catch (e) {
    console.error('[CalendarCache] Error saving calendar cache:', e);
  }
};

// Pulisce la cache dei calendari
const clearCalendarListCache = () => {
  localStorage.removeItem(CALENDAR_LIST_CACHE_KEY);
  localStorage.removeItem(CALENDAR_LIST_CACHE_TIME_KEY);
  console.log('[CalendarCache] Cache cleared');
};

// ============================================
// MICROSOFT DELTA LINK (localStorage, per-utente)
// Speculare a google_calendar_sync_token: persiste il delta_link del calendario
// primario Outlook per il sync incrementale. Chiave per-utente per evitare leak
// tra account diversi sullo stesso browser.
// ============================================

const microsoftDeltaKey = (userId: string) => `microsoft_calendar_delta_${userId}`;

const loadMicrosoftDelta = (userId: string): string | null => {
  try {
    return localStorage.getItem(microsoftDeltaKey(userId));
  } catch (_e) {
    return null;
  }
};

const saveMicrosoftDelta = (userId: string, deltaLink: string) => {
  try {
    localStorage.setItem(microsoftDeltaKey(userId), deltaLink);
  } catch (_e) { /* ignore quota errors */ }
};

const clearMicrosoftDelta = (userId?: string) => {
  try {
    if (userId) {
      localStorage.removeItem(microsoftDeltaKey(userId));
    }
  } catch (_e) { /* ignore */ }
};

// ============================================
// AUTH DATA CACHE (in-memory)
// Per evitare chiamate duplicate a getUser() e user_profiles
// durante la stessa sessione di navigazione
// ============================================

interface AuthCache {
  userId: string;
  organizationId: string;
  googleAccountConnected: boolean;
  googleCalendarIds: string[];
  // Ramo Microsoft (speculare a google_*)
  microsoftAccountConnected: boolean;
  microsoftCalendarIds: string[];
  timestamp: number;
}

let authCache: AuthCache | null = null;
const AUTH_CACHE_TTL = 60000; // 60 secondi

/**
 * Ottiene i dati auth dalla cache se valida, altrimenti li recupera dal DB.
 * Riduce drasticamente le chiamate a getUser() e user_profiles durante la navigazione.
 */
const getCachedAuthData = async (): Promise<AuthCache | null> => {
  // Se la cache è valida, restituiscila
  if (authCache && Date.now() - authCache.timestamp < AUTH_CACHE_TTL) {
    console.log('[AuthCache] Using cached auth data');
    return authCache;
  }

  console.log('[AuthCache] Fetching fresh auth data');

  // Altrimenti fetch e salva
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user?.id) {
    console.log('[AuthCache] No user found');
    return null;
  }

  const { data: userProfile, error } = await supabase
    .from('user_profiles')
    .select('organization_id, google_account_connected, google_calendar_ids, microsoft_account_connected, microsoft_calendar_ids')
    .eq('id', userData.user.id)
    .single();

  if (error || !userProfile?.organization_id) {
    console.log('[AuthCache] No user profile found:', error?.message);
    return null;
  }

  authCache = {
    userId: userData.user.id,
    organizationId: userProfile.organization_id,
    googleAccountConnected: userProfile.google_account_connected || false,
    googleCalendarIds: userProfile.google_calendar_ids || [],
    microsoftAccountConnected: (userProfile as any).microsoft_account_connected || false,
    microsoftCalendarIds: (userProfile as any).microsoft_calendar_ids || [],
    timestamp: Date.now()
  };

  console.log('[AuthCache] Cached auth data for user:', authCache.userId);
  return authCache;
};

/**
 * Invalida la cache auth (chiamare dopo logout o cambio profilo)
 */
const clearAuthCache = () => {
  authCache = null;
  console.log('[AuthCache] Cache cleared');
};

// ============================================
// MERGE HELPER: Preserva calendari esistenti e lo stato enabled
// ============================================

/**
 * Fa merge dei calendari Google preservando:
 * 1. TUTTI i calendari esistenti (anche quelli non presenti nella risposta API)
 * 2. Lo stato enabled già esistente
 * 3. Aggiorna solo i metadati (nome, colore) quando disponibili dall'API
 * 4. Aggiunge nuovi calendari dall'API
 *
 * Questo previene la perdita di calendari quando l'API restituisce meno calendari della cache.
 */
const mergeGoogleSources = (
  existingSources: CalendarSource[],
  newCalendars: Array<{ id: string; summary: string; backgroundColor: string; primary: boolean }>,
  defaultEnabledIds: string[] // IDs dei calendari che dovrebbero essere enabled di default (dal DB)
): CalendarSource[] => {
  // Mantieni i source non-Google
  const nonGoogleSources = existingSources.filter(s => s.type !== 'google');
  const existingGoogleSources = existingSources.filter(s => s.type === 'google');

  // Mappa dei nuovi calendari dall'API per ID
  const newCalendarsMap = new Map(newCalendars.map(c => [c.id, c]));

  // Mappa dei calendari esistenti per ID
  const existingGoogleMap = new Map(
    existingGoogleSources
      .filter(s => s.googleCalendarId)
      .map(s => [s.googleCalendarId!, s])
  );

  const updatedGoogleSources: CalendarSource[] = [];

  // 1. Preserva e aggiorna TUTTI i calendari esistenti
  for (const existingSource of existingGoogleSources) {
    const calId = existingSource.googleCalendarId;
    if (!calId) continue;

    const newCal = newCalendarsMap.get(calId);
    if (newCal) {
      // Calendario esiste sia in cache che nell'API → aggiorna metadati, preserva enabled
      updatedGoogleSources.push({
        ...existingSource,
        name: newCal.summary || existingSource.name,
        color: newCal.backgroundColor || existingSource.color,
        // PRESERVA lo stato enabled esistente
      });
    } else {
      // Calendario esiste in cache ma NON nell'API → MANTIENI comunque
      // (potrebbe essere un errore temporaneo dell'API o rate limit)
      updatedGoogleSources.push(existingSource);
    }
  }

  // 2. Aggiungi nuovi calendari che non esistevano prima
  let newCalendarsAdded = 0;
  for (const newCal of newCalendars) {
    if (!existingGoogleMap.has(newCal.id)) {
      updatedGoogleSources.push({
        id: `google-${newCal.id}`,
        name: newCal.summary || 'Google Calendar',
        color: newCal.backgroundColor || '#4285F4',
        enabled: defaultEnabledIds.length > 0
          ? defaultEnabledIds.includes(newCal.id)
          : newCal.primary === true,
        type: 'google' as const,
        googleCalendarId: newCal.id
      });
      newCalendarsAdded++;
    }
  }

  console.log('[CalendarMerge] Result:', updatedGoogleSources.length, 'calendars',
    '(preserved:', existingGoogleSources.length, ', new:', newCalendarsAdded, ')');

  return [...nonGoogleSources, ...updatedGoogleSources];
};

interface GoogleEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  location?: string;
  organizer?: {
    email: string;
    displayName?: string;
  };
  colorId?: string;
  source?: {
    title: string;
    url: string;
  };
  calendar?: string; // ID of the calendar this event belongs to
}

// Evento Microsoft Graph "raw" restituito dalla edge microsoft-calendar-events.
interface MicrosoftGraphEvent {
  id: string; // ImmutableId
  subject?: string;
  bodyPreview?: string;
  start?: { dateTime?: string; timeZone?: string };
  end?: { dateTime?: string; timeZone?: string };
  isAllDay?: boolean;
  location?: { displayName?: string };
  onlineMeeting?: { joinUrl?: string } | null;
  type?: 'singleInstance' | 'occurrence' | 'exception' | 'seriesMaster';
  isCancelled?: boolean;
  webLink?: string;
}

export interface CalendarSource {
  id: string;
  name: string;
  color: string;
  enabled: boolean;
  type: 'crm' | 'google' | 'microsoft' | 'sales_activities' | 'project_tasks' | 'bookings' | 'alfadocs';
  googleCalendarId?: string;
  // Ramo Microsoft (speculare a googleCalendarId)
  microsoftCalendarId?: string;
  isPrimaryMicrosoft?: boolean;
}

/**
 * Merge dei calendari Microsoft, speculare a mergeGoogleSources:
 * 1. Preserva TUTTI i source non-microsoft e i microsoft esistenti (con enabled)
 * 2. Aggiorna metadati (nome, colore) dei calendari già noti
 * 3. Aggiunge i nuovi calendari dall'API (enabled se in defaultEnabledIds, altrimenti primario)
 */
const mergeMicrosoftSources = (
  existingSources: CalendarSource[],
  newCalendars: Array<{ id: string; name: string; color: string; isDefault: boolean }>,
  defaultEnabledIds: string[]
): CalendarSource[] => {
  const nonMicrosoftSources = existingSources.filter(s => s.type !== 'microsoft');
  const existingMicrosoftSources = existingSources.filter(s => s.type === 'microsoft');

  const existingMap = new Map(
    existingMicrosoftSources
      .filter(s => s.microsoftCalendarId)
      .map(s => [s.microsoftCalendarId!, s])
  );

  const updated: CalendarSource[] = [];

  // 1. Preserva/aggiorna gli esistenti
  for (const existingSource of existingMicrosoftSources) {
    const calId = existingSource.microsoftCalendarId;
    if (!calId) continue;
    const newCal = newCalendars.find(c => c.id === calId);
    if (newCal) {
      updated.push({
        ...existingSource,
        name: newCal.name || existingSource.name,
        color: newCal.color || existingSource.color,
        isPrimaryMicrosoft: newCal.isDefault === true,
      });
    } else {
      updated.push(existingSource);
    }
  }

  // 2. Aggiungi i nuovi
  for (const newCal of newCalendars) {
    if (!existingMap.has(newCal.id)) {
      updated.push({
        id: `microsoft-${newCal.id}`,
        name: newCal.name || 'Outlook Calendar',
        color: newCal.color || '#0078D4',
        enabled: defaultEnabledIds.length > 0
          ? defaultEnabledIds.includes(newCal.id)
          : newCal.isDefault === true,
        type: 'microsoft' as const,
        microsoftCalendarId: newCal.id,
        isPrimaryMicrosoft: newCal.isDefault === true,
      });
    }
  }

  return [...nonMicrosoftSources, ...updated];
};

// Colore hex Microsoft dal campo `color`/`hexColor` (Graph usa nomi tipo "auto"/"lightBlue").
const MICROSOFT_COLOR_MAP: Record<string, string> = {
  lightBlue: '#A6C8FF', lightGreen: '#A7E3A5', lightOrange: '#FFC08A', lightGray: '#C8C8C8',
  lightYellow: '#FFE699', lightTeal: '#9FE2E2', lightPink: '#F5B7D0', lightBrown: '#C9A88F',
  lightRed: '#F5A3A3', maxColor: '#0078D4', auto: '#0078D4',
};
const getMicrosoftCalendarColor = (color?: string, hexColor?: string): string => {
  if (hexColor && /^#[0-9a-fA-F]{6}$/.test(hexColor)) return hexColor;
  if (color && MICROSOFT_COLOR_MAP[color]) return MICROSOFT_COLOR_MAP[color];
  return '#0078D4'; // Outlook blue di default
};

/**
 * Graph restituisce dateTime SENZA offset (default UTC quando la edge non forza
 * outlook.timezone). Aggiunge 'Z' se manca un offset esplicito così JS lo
 * interpreta come UTC e lo converte in locale per la visualizzazione (come Google).
 */
const parseGraphDateTime = (dt: string): string => {
  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(dt)) return dt;
  return dt + 'Z';
};

/**
 * Normalizza un evento Microsoft Graph nello shape interno CalendarEvent,
 * speculare alla normalizzazione degli eventi Google in fetchGoogleEvents.
 * Ritorna null per eventi da NON mostrare (seriesMaster, cancellati).
 */
const normalizeMicrosoftEvent = (
  ev: MicrosoftGraphEvent,
  calId: string,
  source?: CalendarSource
): CalendarEvent | null => {
  if (!ev || !ev.id) return null;
  if (ev.type === 'seriesMaster') return null; // il master non è un'istanza da mostrare
  if (ev.isCancelled === true) return null;

  const isAllDay = ev.isAllDay === true;
  const startStr = ev.start?.dateTime;
  const endStr = ev.end?.dateTime;
  const start = startStr ? new Date(parseGraphDateTime(startStr)) : new Date();
  const end = endStr ? new Date(parseGraphDateTime(endStr)) : start;
  const joinUrl = ev.onlineMeeting?.joinUrl || undefined;

  return {
    id: `microsoft-${ev.id}`,
    title: ev.subject || '(Nessun titolo)',
    description: ev.bodyPreview || '',
    start_time: start,
    end_time: end,
    all_day: isAllDay,
    location: ev.location?.displayName || '',
    created_at: new Date().toISOString(),
    source: 'microsoft',
    source_id: calId, // ID calendario Outlook di origine
    source_name: source?.name || 'Outlook Calendar',
    color: source?.color || '#0078D4',
    is_editable: false,
    metadata: {
      microsoft_event_id: ev.id,
      web_link: ev.webLink,
      join_url: joinUrl,
      meet_link: joinUrl,
    },
  };
};

interface CalendarStore {
  events: CalendarEvent[];
  googleEvents: CalendarEvent[];
  microsoftEvents: CalendarEvent[];
  salesActivities: CalendarEvent[];
  projectTasks: CalendarEvent[];
  bookingEvents: CalendarEvent[];
  workSlots: WorkSlot[];
  loading: boolean;
  googleLoading: boolean;
  microsoftLoading: boolean;
  salesActivitiesLoading: boolean;
  projectTasksLoading: boolean;
  bookingEventsLoading: boolean;
  alfadocsEvents: CalendarEvent[];
  alfadocsLoading: boolean;
  // Practice_id della connessione AlfaDocs attiva più vecchia dell'org (per
  // normalizzare i source_id/link legacy pre-multi-connessione). Aggiornato
  // ad ogni fetchAlfaDocsAppointments.
  alfadocsOldestPracticeId: string | null;
  workSlotsLoading: boolean;
  error: string | null;
  googleError: string | null;
  microsoftError: string | null;
  salesActivitiesError: string | null;
  projectTasksError: string | null;
  bookingEventsError: string | null;
  workSlotsError: string | null;
  calendarSources: CalendarSource[];
  setCalendarSourceEnabled: (id: string, enabled: boolean) => void;
  bookingStatusFilters: string[];
  setBookingStatusFilters: (statuses: string[]) => void;
  // Studi AlfaDocs visibili (org multi-connessione). null = tutti (default,
  // nessun impatto mono-studio). Non persistito: al refresh tornano tutti.
  alfadocsStudioFilters: string[] | null;
  setAlfadocsStudioFilters: (practiceIds: string[] | null) => void;
  getEnabledEvents: () => CalendarEvent[];
  fetchEvents: (startDate: Date, endDate: Date) => Promise<void>;
  fetchGoogleEvents: (startDate: Date, endDate: Date, options?: { skipCalendarListFetch?: boolean }) => Promise<void>;
  fetchMicrosoftEvents: (startDate: Date, endDate: Date, options?: { skipCalendarListFetch?: boolean; forceRefresh?: boolean }) => Promise<void>;
  fetchSalesActivities: (startDate: Date, endDate: Date) => Promise<void>;
  fetchProjectTasks: (startDate: Date, endDate: Date) => Promise<void>;
  fetchBookingEvents: (startDate: Date, endDate: Date, filterByUserId?: string) => Promise<void>;
  fetchAlfaDocsAppointments: (startDate: Date, endDate: Date) => Promise<void>;
  fetchClientEvents: (clientId: string) => Promise<void>;
  createEvent: (event: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>) => Promise<CalendarEvent | null>;
  updateEvent: (id: string, event: Partial<CalendarEvent>) => Promise<CalendarEvent | null>;
  deleteEvent: (id: string) => Promise<boolean>;
  // Google Calendar operations
  createGoogleEvent: (calendarId: string, event: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>, assignedUserId?: string) => Promise<CalendarEvent | null>;
  updateGoogleEvent: (eventId: string, calendarId: string, event: Partial<CalendarEvent>) => Promise<CalendarEvent | null>;
  deleteGoogleEvent: (eventId: string, calendarId: string) => Promise<boolean>;
  getGoogleEvent: (eventId: string, calendarId: string) => Promise<any | null>;
  // Microsoft (Outlook) Calendar operations — speculari a quelle Google
  createMicrosoftEvent: (calendarId: string | undefined, event: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>, options?: { createTeamsMeeting?: boolean; activityId?: string; targetUserId?: string }) => Promise<{ event: CalendarEvent; eventId: string; joinUrl?: string } | null>;
  updateMicrosoftEvent: (eventId: string, event: Partial<CalendarEvent>, targetUserId?: string) => Promise<CalendarEvent | null>;
  deleteMicrosoftEvent: (eventId: string, targetUserId?: string) => Promise<boolean>;
  getMicrosoftEvent: (eventId: string) => Promise<any | null>;
  // Work Slots operations
  fetchWorkSlots: (startDate: Date, endDate: Date, assignedTo?: string) => Promise<void>;
  createWorkSlot: (data: CreateWorkSlotData) => Promise<WorkSlot | null>;
  updateWorkSlot: (id: string, data: Partial<CreateWorkSlotData>) => Promise<WorkSlot | null>;
  deleteWorkSlot: (id: string) => Promise<boolean>;
  // Sync operations
  searchEvents: (query: string) => CalendarEvent[];
  getSyncToken: () => string | null;
  useSyncToken: (token: string) => void;
  clearSyncToken: () => void; // New method to clear sync token on error
  // Error utility
  resetErrors: () => void; // New method to reset error states
  // Store reset (for logout)
  resetStore: () => void;
}

/**
 * Helper function to perform a retry with exponential backoff
 */
async function withRetry<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    onError?: (error: unknown, attempt: number) => void;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 300,
    maxDelay = 5000,
    onError
  } = options;

  let attempt = 0;
  let delay = initialDelay;

  while (true) {
    try {
      return await operation();
    } catch (error) {
      attempt++;
      
      // Call onError callback if provided
      if (onError) {
        onError(error, attempt);
      }
      
      // If we've reached max retries, throw
      if (attempt >= maxRetries) {
        throw error;
      }
      
      // Log retry attempt
      console.log(`Retry attempt ${attempt}/${maxRetries} - waiting ${delay}ms before next attempt`);
      
      // Wait with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase delay for next attempt (with max cap)
      delay = Math.min(delay * 2, maxDelay);
    }
  }
}

// Risolve la connessione AlfaDocs per il write-back di un CLIENTE: practice
// primaria del cliente (o unica linkata); fallback alla più vecchia connessione
// ATTIVA collegata se la primaria è stata disconnessa. Ritorna null se il
// cliente non è collegato a nessuna practice attiva.
async function resolveClientAlfaConfig(
  clientId: string,
  organizationId: string
): Promise<{ config: AlfaDocsConfig; patientId: string } | null> {
  const { data: clientInfo } = await supabase.from('clients').select('metadata').eq('id', clientId).single();
  const configs = (await AlfaDocsService.listConfigs(organizationId)).filter(c => c.is_active);
  if (configs.length === 0) return null;
  const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
  const primary = getPrimaryPracticeId(clientInfo?.metadata, oldestPracticeId);
  if (!primary) return null;
  const links = getAlfaDocsLinks(clientInfo?.metadata, oldestPracticeId);
  const cfg = configs.find(c => c.configuration?.practice_id === primary)
    || configs.find(c => links[c.configuration?.practice_id || '']) || null;
  if (!cfg || !cfg.configuration?.practice_id) return null;
  const patientId = getPatientIdForPractice(clientInfo?.metadata, cfg.configuration.practice_id, oldestPracticeId);
  return patientId ? { config: cfg, patientId } : null;
}

// Risolve il configId AlfaDocs per il write-back di un EVENTO già collegato:
// metadata.alfadocs_practice_id → source_id parsato → fallback alla connessione
// attiva più vecchia dell'org (comportamento storico single-connection).
function resolveEventAlfaConfigId(
  event: { metadata?: any; source_id?: string | null } | null | undefined,
  configs: AlfaDocsConfig[]
): string | undefined {
  const activeConfigs = configs.filter(c => c.is_active);
  if (activeConfigs.length === 0) return undefined;
  const oldestPracticeId = activeConfigs[0]?.configuration?.practice_id ?? null;
  const practiceId = (event?.metadata as any)?.alfadocs_practice_id
    || parseAlfaSourceId(event?.source_id)?.practiceId
    || oldestPracticeId;
  const cfg = activeConfigs.find(c => c.configuration?.practice_id === practiceId) || activeConfigs[0];
  return cfg?.id;
}

export const calendarStore = create<CalendarStore>((set, get) => ({
  events: [],
  googleEvents: [],
  microsoftEvents: [],
  salesActivities: [],
  projectTasks: [],
  bookingEvents: [],
  workSlots: [],
  loading: false,
  googleLoading: false,
  microsoftLoading: false,
  salesActivitiesLoading: false,
  projectTasksLoading: false,
  bookingEventsLoading: false,
  alfadocsEvents: [],
  alfadocsLoading: false,
  alfadocsOldestPracticeId: null,
  workSlotsLoading: false,
  error: null,
  googleError: null,
  microsoftError: null,
  salesActivitiesError: null,
  projectTasksError: null,
  bookingEventsError: null,
  workSlotsError: null,
  calendarSources: [
    {
      id: 'crm',
      name: 'CRM Calendar',
      color: '#000000',
      enabled: true,
      type: 'crm'
    },
    {
      id: 'sales_activities',
      name: 'Task Offerte, Lead e Clienti',
      color: '#3B82F6',
      enabled: true,
      type: 'sales_activities'
    },
    {
      id: 'project_tasks',
      name: 'Project Tasks',
      color: '#10B981',
      enabled: false,
      type: 'project_tasks'
    },
    {
      id: 'bookings',
      name: 'Prenotazioni',
      color: '#F59E0B',
      enabled: true,
      type: 'bookings'
    },
    {
      id: 'alfadocs',
      name: 'AlfaDocs',
      color: '#2383E2',
      enabled: true,
      type: 'alfadocs'
    }
  ],
  // Store sync token for incremental sync
  _syncToken: localStorage.getItem('google_calendar_sync_token') || null,

  bookingStatusFilters: ['confirmed', 'pending'],

  setBookingStatusFilters: (statuses) => {
    set({ bookingStatusFilters: statuses });
  },

  alfadocsStudioFilters: null,

  setAlfadocsStudioFilters: (practiceIds) => {
    set({ alfadocsStudioFilters: practiceIds });
  },

  setCalendarSourceEnabled: (id, enabled) => {
    set({
      calendarSources: get().calendarSources.map(source => 
        source.id === id ? { ...source, enabled } : source
      )
    });
  },

  getEnabledEvents: () => {
    const { events, googleEvents, microsoftEvents, salesActivities, projectTasks, bookingEvents, alfadocsEvents, calendarSources } = get();
    const enabledSources = calendarSources.filter(source => source.enabled);

    const enabledEvents = [];

    // Add CRM events if enabled (separate AlfaDocs events)
    const alfadocsEnabled = enabledSources.some(source => source.type === 'alfadocs' && source.enabled);
    const crmEnabled = enabledSources.some(source => source.type === 'crm' && source.enabled);

    // Filtro studi AlfaDocs (multi-connessione): risolve la practice dell'evento
    // come nel dedup sotto (metadata → source_id → connessione più vecchia).
    const { alfadocsStudioFilters, alfadocsOldestPracticeId: oldestPid } = get();
    const passesStudioFilter = (event: CalendarEvent): boolean => {
      if (!alfadocsStudioFilters) return true;
      const practiceId = (event.metadata as any)?.alfadocs_practice_id
        || parseAlfaSourceId(event.source_id)?.practiceId
        || oldestPid;
      return practiceId != null && alfadocsStudioFilters.includes(String(practiceId));
    };

    if (crmEnabled || alfadocsEnabled) {
      events.forEach(event => {
        const isAlfadocs = event.source_id?.startsWith('alfadocs_');
        if (isAlfadocs && alfadocsEnabled) {
          if (passesStudioFilter(event)) enabledEvents.push(event);
        } else if (!isAlfadocs && crmEnabled) {
          // Eventi del CRM: agenda di tutta l'azienda, come prima del calendario per persona
          enabledEvents.push(event);
        }
      });
    }

    // Add AlfaDocs live events if enabled (deduplicate with CRM-synced alfadocs events).
    // I source_id CRM possono essere in formato nuovo (alfadocs_<practiceId>_<aptId>)
    // o legacy (alfadocs_<aptId>, senza practiceId): normalizziamo entrambi i lati
    // con parseAlfaSourceId, mappando il legacy (practiceId null) sulla connessione
    // attiva più vecchia dell'org così da matchare comunque il live corrispondente.
    if (alfadocsEnabled && alfadocsEvents.length > 0) {
      const { alfadocsOldestPracticeId } = get();
      const alfaKey = (sourceId: string | null | undefined): string | null => {
        const parsed = parseAlfaSourceId(sourceId);
        if (!parsed) return null;
        return `${parsed.practiceId ?? alfadocsOldestPracticeId ?? 'legacy'}:${parsed.appointmentId}`;
      };
      const existingAlfaKeys = new Set(
        events
          .filter(e => e.source_id?.startsWith('alfadocs_'))
          .map(e => alfaKey(e.source_id))
          .filter((k): k is string => k !== null)
      );
      alfadocsEvents.forEach(event => {
        const key = alfaKey(event.source_id);
        if ((!key || !existingAlfaKeys.has(key)) && passesStudioFilter(event)) {
          enabledEvents.push(event);
        }
      });
    }

    // Add Sales Activities if enabled. Overlay: il colore scelto a mano su Google
    // Calendar (evento gemello nascosto dalla dedup sotto) vince sul blu fisso CRM.
    // Il calendario per persona vale per le task commerciali; eventi, prenotazioni e task di progetto restano di tutta l'azienda
    const shownSales = presentSalesActivities(salesActivities);
    if (enabledSources.some(source => source.type === 'sales_activities' && source.enabled)) {
      enabledEvents.push(...overlayGoogleEventColors(shownSales, googleEvents));
    }

    // Add Project Tasks if enabled
    if (enabledSources.some(source => source.type === 'project_tasks' && source.enabled)) {
      enabledEvents.push(...projectTasks);
    }

    // Add Booking Events if enabled (filtered by status)
    if (enabledSources.some(source => source.type === 'bookings' && source.enabled)) {
      const { bookingStatusFilters } = get();
      const filteredBookings = bookingEvents.filter(e =>
        bookingStatusFilters.includes(e.metadata?.status as string)
      );
      enabledEvents.push(...filteredBookings);
    }
    
    // Build set of Google event IDs linked to bookings (for deduplication).
    // Nota prefissi: gli id in DB possono essere puliti (righe normalizzate) o
    // legacy 'google-<id>' — normalizza prima di ricostruire l'id dello store.
    const toStoreGoogleId = (gid: string) => `google-${gid.replace(/^google-/, '')}`;
    const bookingGoogleIds = new Set(
      bookingEvents
        .map(e => e.metadata?.google_event_id)
        .filter(Boolean)
        .map(gid => toStoreGoogleId(gid as string))
    );
    const bookingsEnabled = enabledSources.some(source => source.type === 'bookings' && source.enabled);

    // Dedup attività CRM pushate su Google: senza questo set, un'attività
    // sincronizzata compare due volte (attività CRM + evento Google) —
    // speculare al ramo Microsoft sotto.
    const salesActivitiesEnabled = enabledSources.some(source => source.type === 'sales_activities' && source.enabled);
    // Solo le task mostrate: se la task e' nascosta (calendario spento o non visibile) il suo gemello Google resta
    const crmGoogleIds = new Set(
      shownSales
        .map(e => (e.metadata as any)?.google_event_id)
        .filter(Boolean)
        .map((gid: string) => toStoreGoogleId(gid))
    );

    // Add Google events from enabled sources
    const enabledGoogleCalendarIds = enabledSources
      .filter(source => source.type === 'google' && source.enabled && source.googleCalendarId)
      .map(source => source.googleCalendarId);

    if (enabledGoogleCalendarIds.length > 0) {
      // Filter Google events by calendar ID, skip duplicates of booking/CRM events
      googleEvents.forEach(event => {
        if (event.source_id && enabledGoogleCalendarIds.includes(event.source_id)) {
          if (bookingsEnabled && bookingGoogleIds.has(event.id)) return;
          if (salesActivitiesEnabled && crmGoogleIds.has(event.id)) return;
          enabledEvents.push(event);
        }
      });
    }

    // Add Microsoft (Outlook) events from enabled sources — speculare al ramo Google
    const enabledMicrosoftCalendarIds = enabledSources
      .filter(source => source.type === 'microsoft' && source.enabled && source.microsoftCalendarId)
      .map(source => source.microsoftCalendarId);

    if (enabledMicrosoftCalendarIds.length > 0) {
      // Dedup: le attività CRM già pushate su Outlook non devono comparire due volte
      // (evento Outlook + attività CRM). Filtra gli eventi il cui id compare in
      // sales_activities.microsoft_event_id — ESATTAMENTE come il ramo Google/bookings
      // fa con google_event_id.
      // Solo le task mostrate, come per Google
      const crmMicrosoftIds = new Set(
        shownSales
          .map(e => (e.metadata as any)?.microsoft_event_id)
          .filter(Boolean)
          .map((mid: string) => `microsoft-${mid}`)
      );

      microsoftEvents.forEach(event => {
        if (event.source_id && enabledMicrosoftCalendarIds.includes(event.source_id)) {
          if (crmMicrosoftIds.has(event.id)) return;
          enabledEvents.push(event);
        }
      });
    }

    return enabledEvents;
  },

  // Reset error states
  resetErrorStates: () => {
    set({ error: null, googleError: null, microsoftError: null });
  },

  // Clear sync token to force full refresh
  clearSyncTokenStorage: () => {
    localStorage.removeItem('google_calendar_sync_token');
    set({ _syncToken: null });
    console.log('Google Calendar sync token cleared');
  },

  // Keep track of last fetch range to avoid redundant calls
  fetchEvents: async (startDate, endDate) => {
    // Store static properties on the function itself to track last fetch time and range
    const now = Date.now();
    const lastFetchTime = (useCalendarStore as any).lastFetchTime || 0;
    const lastStartDate = (useCalendarStore as any).lastStartDate;
    const lastEndDate = (useCalendarStore as any).lastEndDate;

    // Minimum time between fetches (ms)
    const FETCH_COOLDOWN = 2000;

    // Check if we've fetched this range recently (within 2 seconds)
    const isSameRange = lastStartDate && lastEndDate &&
                        Math.abs(startDate.getTime() - lastStartDate.getTime()) < 86400000 &&
                        Math.abs(endDate.getTime() - lastEndDate.getTime()) < 86400000;

    if (isSameRange && now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('Skipping duplicate fetch within cooldown period');
      return;
    }

    // Update tracking variables
    (useCalendarStore as any).lastFetchTime = now;
    (useCalendarStore as any).lastStartDate = startDate;
    (useCalendarStore as any).lastEndDate = endDate;

    set({ loading: true, error: null });
    try {
      // Get cached auth data to avoid duplicate API calls
      const authData = await getCachedAuthData();
      if (!authData) throw new Error('User not authenticated');

      const organizationId = authData.organizationId;

      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('organization_id', organizationId)
        .gte('start_time', startDate.toISOString())
        .lte('end_time', endDate.toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;
      set({ events: data || [], loading: false });
      
      // Also fetch Google events, but don't block on it
      // Wait a small delay to avoid simultaneous API calls
      // NOTA: Passiamo skipCalendarListFetch: true perché la lista calendari
      // viene caricata SOLO al primo caricamento della pagina (isInitialLoad = true)
      // o quando l'utente clicca "Aggiorna"
      setTimeout(() => {
        try {
          get().fetchGoogleEvents(startDate, endDate, { skipCalendarListFetch: true }).catch(err => {
            console.error("Error fetching Google events (silently handled):", err);
            // Don't rethrow, we still want to show internal calendar events

            // Parse and store the error in googleError state
            const parsedError = parseErrorResponse(err);
            set({
              googleError: parsedError.message,
              googleLoading: false
            });
          });
        } catch (googleError) {
          console.error("Error initiating Google events fetch:", googleError);
          // Set googleError state, but don't fail the whole calendar
          const parsedError = parseErrorResponse(googleError);
          set({
            googleError: parsedError.message,
            googleLoading: false
          });
        }

        // Ramo Microsoft (speculare a Google, sync indipendente)
        try {
          get().fetchMicrosoftEvents(startDate, endDate, { skipCalendarListFetch: true }).catch(err => {
            console.error("Error fetching Microsoft events (silently handled):", err);
            const parsedError = parseErrorResponse(err);
            set({ microsoftError: parsedError.message, microsoftLoading: false });
          });
        } catch (microsoftError) {
          console.error("Error initiating Microsoft events fetch:", microsoftError);
          const parsedError = parseErrorResponse(microsoftError);
          set({ microsoftError: parsedError.message, microsoftLoading: false });
        }
      }, 500);
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ error: parsedError.message, loading: false });
    }
  },
  
  fetchGoogleEvents: async (startDate, endDate, options?: { skipCalendarListFetch?: boolean; forceRefresh?: boolean }) => {
    // Reset google error state at the start of a new fetch
    set({ googleError: null });

    // Extract options
    const skipCalendarListFetch = options?.skipCalendarListFetch || false;
    const forceRefresh = options?.forceRefresh || false;
    
    // Get settings to check if Google calendar is enabled
    const settings = useSettingsStore.getState().settings;
    if (!settings?.sync_calendar) {
      return;
    }
    
    // Only allow one fetch at a time with a global lock
    if ((useCalendarStore as any).googleFetchInProgress) {
      console.log('Google Calendar fetch already in progress, skipping redundant fetch');
      return;
    }
    
    // Set the lock
    (useCalendarStore as any).googleFetchInProgress = true;

    // ========== CACHE-FIRST: Mostra calendari cached immediatamente ==========
    // MA NON se forceRefresh è true - in quel caso lo stato è già corretto
    // e non vogliamo sovrascriverlo con valori dalla cache

    // Ottieni sempre cachedAuthData (serve per il resto della funzione)
    const cachedAuthData = await getCachedAuthData();

    // Solo popolare dalla cache se NON è forceRefresh E se non ci sono già calendari Google
    // Questo previene la sovrascrittura di toggle utente
    if (!forceRefresh) {
      const existingGoogleSources = get().calendarSources.filter(s => s.type === 'google');

      // SOLO popolare dalla cache se non ci sono già calendari Google nello stato
      // Se l'utente ha già toggleato calendari, non sovrascriverli con valori dalla cache
      if (existingGoogleSources.length === 0 && cachedAuthData?.userId) {
        const cachedCalendars = loadCalendarListCache(cachedAuthData.userId);
        const selectedIds = cachedAuthData?.googleCalendarIds || [];

        if (cachedCalendars && cachedCalendars.length > 0) {
          // Popola immediatamente la sidebar con i calendari cached
          const cachedSources = cachedCalendars.map(cal => ({
            id: `google-${cal.id}`,
            name: cal.summary || 'Google Calendar',
            color: cal.backgroundColor || '#4285F4',
            enabled: selectedIds.length > 0
              ? selectedIds.includes(cal.id)
              : cal.primary === true,
            type: 'google' as const,
            googleCalendarId: cal.id
          }));

          const existingSources = get().calendarSources.filter(s => s.type !== 'google');
          set({ calendarSources: [...existingSources, ...cachedSources] });
          console.log('[CalendarCache] Instantly showed', cachedSources.length, 'calendars from cache');
        }
      } else {
        console.log('[CalendarCache] Skipping cache-first - Google sources already exist in state:', existingGoogleSources.length);
      }
    } else {
      console.log('[forceRefresh] Skipping cache-first to preserve current calendarSources state');
    }
    // ========== Fine CACHE-FIRST ==========

    // Store static properties on the function itself to track last Google fetch time and range
    const now = Date.now();
    const lastGoogleFetchTime = (useCalendarStore as any).lastGoogleFetchTime || 0;
    const lastGoogleStartDate = (useCalendarStore as any).lastGoogleStartDate;
    const lastGoogleEndDate = (useCalendarStore as any).lastGoogleEndDate;
    
    // Minimum time between Google fetches (ms) - reduced to allow modal refreshes
    const GOOGLE_FETCH_COOLDOWN = 5000; // 5 seconds between Google Calendar fetches
    
    // Check if we've fetched this range recently with a very permissive range check
    const isSameGoogleRange = lastGoogleStartDate && lastGoogleEndDate && 
                      Math.abs(startDate.getTime() - lastGoogleStartDate.getTime()) < 86400000 * 7 && // 7 days difference
                      Math.abs(endDate.getTime() - lastGoogleEndDate.getTime()) < 86400000 * 7;     // 7 days difference
                      
    // Se forceRefresh, ignora il cooldown (es. quando si abilita un nuovo calendario)
    if (!forceRefresh && isSameGoogleRange && now - lastGoogleFetchTime < GOOGLE_FETCH_COOLDOWN) {
      console.log('Skipping duplicate Google Calendar fetch within cooldown period');
      (useCalendarStore as any).googleFetchInProgress = false; // Release lock
      return;
    }
    
    // Update tracking variables
    (useCalendarStore as any).lastGoogleFetchTime = now;
    (useCalendarStore as any).lastGoogleStartDate = startDate;
    (useCalendarStore as any).lastGoogleEndDate = endDate;
    
    set({ googleLoading: true, googleError: null });

    try {
      // Usa auth cache per evitare chiamate API duplicate
      // (cachedAuthData è già stato ottenuto sopra nella cache-first section)
      if (!cachedAuthData) {
        console.log('Google Calendar fetch - No auth data available');
        set({ googleLoading: false });
        (useCalendarStore as any).googleFetchInProgress = false; // Release the lock
        return;
      }

      console.log('Google Calendar fetch - Using cached auth data:', {
        organization_id: cachedAuthData.organizationId,
        has_calendar_ids: cachedAuthData.googleCalendarIds.length > 0
      });

      // NOTA: NON controlliamo googleAccountConnected qui perché potrebbe essere null nel DB
      // Lasciamo che l'API di Google fallisca se necessario - l'errore verrà gestito nel catch

      if (!cachedAuthData.organizationId) {
        console.warn('User has no organization_id, cannot fetch Google Calendar');
        set({ googleLoading: false, googleError: 'User not associated with an organization' });
        (useCalendarStore as any).googleFetchInProgress = false; // Release the lock
        return;
      }

      // Anche se non ci sono calendar_ids selezionati, recuperiamo comunque gli eventi Google
      // e lasciamo che sia la UI a decidere quali mostrare
      // Se forceRefresh, leggi gli IDs dei calendari abilitati dallo stato attuale
      // (lo stato è già aggiornato dopo il toggle, la cache auth no)
      let calendarIds: string[];
      if (forceRefresh) {
        calendarIds = get().calendarSources
          .filter(s => s.type === 'google' && s.enabled && s.googleCalendarId)
          .map(s => s.googleCalendarId!)
          .filter(id => id !== undefined);
        console.log('[forceRefresh] Using calendar IDs from current state:', calendarIds);
      } else {
        calendarIds = cachedAuthData.googleCalendarIds || [];
      }

      if (!calendarIds || !Array.isArray(calendarIds)) {
        calendarIds = [];
      }

      // ========== FETCH LISTA CALENDARI ==========
      // Skip se stiamo solo navigando (i calendari sono già in cache/stato)
      // La lista viene caricata solo:
      // 1. Al primo caricamento della pagina
      // 2. Quando l'utente fa refresh manuale
      if (!skipCalendarListFetch) {
        try {
          console.log('[google-calendar-list] Fetching fresh calendar list...');
          const { data: calendarData, error: calendarError } = await supabase.functions.invoke('google-calendar-list', {
            body: {
              organization_id: cachedAuthData.organizationId,
              user_id: cachedAuthData.userId
            }
          });

          // Popola la sidebar con tutti i calendari disponibili
          if (!calendarError && calendarData?.calendars && Array.isArray(calendarData.calendars) && calendarData.calendars.length > 0) {
            console.log('Found calendars from google-calendar-list:', calendarData.calendars.length);

            // Usa merge per preservare lo stato enabled esistente (dalla cache)
            const selectedIds = calendarIds.length > 0 ? calendarIds : [];

            const calendarsForMerge = calendarData.calendars.map((calendar: any) => ({
              id: calendar.id,
              summary: calendar.summary || 'Google Calendar',
              backgroundColor: calendar.backgroundColor || '#4285F4',
              primary: calendar.primary === true
            }));

            // MERGE invece di REPLACE - preserva lo stato enabled dalla cache
            const mergedSources = mergeGoogleSources(get().calendarSources, calendarsForMerge, selectedIds);
            set({ calendarSources: mergedSources });
            console.log('[google-calendar-list] Merged calendar sources, total:', mergedSources.filter(s => s.type === 'google').length);

            // ========== Salva nella cache per i prossimi caricamenti ==========
            const calendarsToCache: CachedCalendar[] = calendarData.calendars.map((cal: any) => ({
              id: cal.id,
              summary: cal.summary || 'Google Calendar',
              backgroundColor: cal.backgroundColor || '#4285F4',
              primary: cal.primary === true
            }));
            saveCalendarListCache(calendarsToCache, cachedAuthData.userId);
            // ========== Fine salvataggio cache ==========
          }
        } catch (listError) {
          console.error('Error fetching Google calendar list:', listError);
          // Non interrompiamo l'esecuzione, continuiamo a caricare gli eventi
        }
      } else {
        console.log('[google-calendar-list] Skipped - using cached calendar list');
      }
      // ========== FINE FETCH LISTA CALENDARI ==========
      
      // Get the sync token if available for incremental sync
      const syncToken = get()._syncToken;
      let syncTokenUsed = false;
      
      if (syncToken) {
        console.log(`Using sync token for incremental sync: ${syncToken.substring(0, 20)}...`);
        syncTokenUsed = true;
      }
      
      // Call our edge function to get Google Calendar events with retry logic
      const { data, error } = await withRetry(
        async () => {
          const response = await supabase.functions.invoke('google-calendar-events', {
            body: {
              organization_id: cachedAuthData.organizationId,
              user_id: cachedAuthData.userId,
              calendar_ids: calendarIds,
              start_time: startDate.toISOString(),
              end_time: endDate.toISOString(),
              sync_token: syncToken // Add sync token if available
            }
          });

          if (response.error) {
            // Handle sync token expiration specifically
            const errorData = parseErrorResponse(response.error);
            if (
              syncTokenUsed &&
              (errorData.message?.includes('sync token') ||
               errorData.message?.includes('Sync token'))
            ) {
              // Clear the sync token and retry with full range
              console.log('Sync token invalid or expired, clearing and retrying with full range');
              get().clearSyncTokenStorage();

              // Retry without sync token
              const retryResponse = await supabase.functions.invoke('google-calendar-events', {
                body: {
                  organization_id: cachedAuthData.organizationId,
                  user_id: cachedAuthData.userId,
                  calendar_ids: calendarIds,
                  start_time: startDate.toISOString(),
                  end_time: endDate.toISOString()
                  // No sync token this time
                }
              });
              
              if (retryResponse.error) {
                throw retryResponse.error;
              }
              
              return retryResponse;
            }
            
            throw response.error;
          }
          
          return response;
        },
        {
          maxRetries: 2,
          initialDelay: 1000,
          onError: (error, attempt) => {
            console.log(`Google Calendar fetch attempt ${attempt} failed:`, error);
            
            // If first retry with sync token fails, clear it and retry without
            if (attempt === 1 && syncTokenUsed) {
              const parsedError = parseErrorResponse(error);
              // Check for sync token related errors
              if (
                parsedError.message?.toLowerCase().includes('sync token') || 
                parsedError.message?.toLowerCase().includes('synctoken') ||
                parsedError.status === 410 // Google's "Gone" status for expired sync tokens
              ) {
                console.log('Sync token issue detected, clearing for next retry');
                get().clearSyncTokenStorage();
              }
            }
          }
        }
      );
      
      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to fetch Google Calendar events');
      }
      
      // Check if we received a new sync token and save it for future calls
      if (data?.nextSyncToken) {
        get().useSyncToken(data.nextSyncToken);
      }
      
      // Get calendar metadata from the response BEFORE checking events
      // This ensures we populate the sidebar even when there are no events
      const calendars = data?.calendars || [];
      console.log(`Received ${calendars.length} calendars metadata from Google Calendar API`);

      // Get the currently selected calendar IDs
      // Se forceRefresh, usa gli IDs dallo stato corrente (già aggiornato dopo il toggle)
      // altrimenti usa quelli dalla cache auth
      let selectedCalendarIds: string[];
      if (forceRefresh) {
        selectedCalendarIds = get().calendarSources
          .filter(s => s.type === 'google' && s.enabled && s.googleCalendarId)
          .map(s => s.googleCalendarId!)
          .filter(id => id !== undefined);
        console.log('[forceRefresh] Using selected IDs from current state for merge:', selectedCalendarIds);
      } else if (cachedAuthData?.googleCalendarIds && Array.isArray(cachedAuthData.googleCalendarIds)) {
        selectedCalendarIds = cachedAuthData.googleCalendarIds;
      } else {
        selectedCalendarIds = [];
      }

      // Process calendar sources even if events are empty - usa MERGE per preservare enabled state
      if (calendars.length > 0) {
        console.log('[google-calendar-events] Processing calendar metadata, found', calendars.length, 'calendars');

        const calendarsForMerge = calendars.map((calendar: any) => ({
          id: calendar.id,
          summary: calendar.summary || 'Google Calendar',
          backgroundColor: calendar.backgroundColor || '#4285F4',
          primary: calendar.primary === true
        }));

        // MERGE invece di REPLACE - preserva lo stato enabled esistente
        const mergedSources = mergeGoogleSources(get().calendarSources, calendarsForMerge, selectedCalendarIds);
        set({ calendarSources: mergedSources });
        console.log('[google-calendar-events] Merged calendar sources, total:', mergedSources.filter(s => s.type === 'google').length);
      }

      if (!data?.events || !Array.isArray(data.events)) {
        console.log('No Google events returned or events is not an array');
        set({ googleEvents: [], googleLoading: false });
        (useCalendarStore as any).googleFetchInProgress = false; // Release the lock
        return;
      }

      // If we got an empty array of events, that's OK - just use it
      if (data.events.length === 0) {
        console.log('Google returned an empty events array');
        set({ googleEvents: [], googleLoading: false });
        (useCalendarStore as any).googleFetchInProgress = false; // Release the lock
        return;
      }

      // Log the complete response from the Google Calendar Events API
      console.log('Google Calendar API response:', data);
      
      // Log the calendar IDs to help debug
      if (calendars.length > 0) {
        calendars.forEach((cal: any, index: number) => {
          console.log(`Calendar ${index + 1}: ${cal.id} - ${cal.summary || 'Unknown'} - Primary: ${cal.primary}`);
        });
      }

      // Note: Calendar sources were already processed above (before the empty events check)
      // Now we handle the fallback case where we have events but no calendar metadata
      let googleSources: any[] = [];

      // If no calendar metadata but we have events, try to create sources from events
      if (calendars.length === 0 && data.events && data.events.length > 0) {
        console.log('No calendar metadata, creating sources from events');
        
        // First check if events have calendar property directly
        const eventsWithCalendarProperty = data.events.filter((event: any) => event.calendar);
        
        if (eventsWithCalendarProperty.length > 0) {
          // Extract unique calendar IDs from events.calendar property
          const uniqueCalendarIds = Array.from(new Set(
            eventsWithCalendarProperty.map((event: any) => event.calendar)
          ));
          
          console.log('Unique calendar IDs from events.calendar property:', uniqueCalendarIds);
          
          // Create calendar sources from these IDs
          googleSources = uniqueCalendarIds.map((calendarId: string) => {
            // Determine if this calendar is enabled
            const isEnabled = selectedCalendarIds.length === 0 || selectedCalendarIds.includes(calendarId);
            
            // Try to get a better name from a sample event's organizer
            const sampleEvent = data.events.find((event: any) => 
              event.calendar === calendarId && event.organizer?.displayName
            );
            
            const calendarName = sampleEvent?.organizer?.displayName || 
                                (calendarId === 'primary' ? 'Primary Calendar' : calendarId);
            
            return {
              id: `google-${calendarId}`,
              name: calendarName,
              color: '#4285F4', // Default Google blue
              enabled: isEnabled,
              type: 'google' as const,
              googleCalendarId: calendarId
            };
          });
        } else {
          // Fallback to using organizer email
          const uniqueCalendarIds = Array.from(new Set(
            data.events
              .filter((event: any) => event.organizer?.email)
              .map((event: any) => event.organizer.email)
          ));
          
          console.log('Unique calendar IDs from events.organizer.email:', uniqueCalendarIds);
          
          if (uniqueCalendarIds.length === 0) {
            console.warn('No calendar IDs found in events, using a default ID');
            uniqueCalendarIds.push('primary');
          }
          
          // Create calendar sources from these IDs
          googleSources = uniqueCalendarIds.map((calendarId: string) => {
            // Determine if this calendar is enabled
            const isEnabled = selectedCalendarIds.length === 0 || selectedCalendarIds.includes(calendarId);
            
            // Get a sample event to get the calendar name
            const sampleEvent = data.events.find((event: any) => 
              event.organizer?.email === calendarId
            );
            
            const calendarName = sampleEvent?.organizer?.displayName || 
                               (calendarId === 'primary' ? 'Primary Calendar' : calendarId);
            
            return {
              id: `google-${calendarId}`,
              name: calendarName,
              color: '#4285F4', // Default Google blue
              enabled: isEnabled,
              type: 'google' as const,
              googleCalendarId: calendarId
            };
          });
        }
      }

      // If we created sources from events (fallback case), update the store with MERGE
      if (googleSources.length > 0) {
        console.log(`[fallback] Created ${googleSources.length} Google calendar sources from events`);

        // Converti in formato per merge
        const calendarsForMerge = googleSources.map((src: any) => ({
          id: src.googleCalendarId,
          summary: src.name,
          backgroundColor: src.color,
          primary: src.name === 'Primary Calendar' || src.googleCalendarId.includes('@gmail.com')
        }));

        // MERGE invece di REPLACE - preserva lo stato enabled esistente
        const mergedSources = mergeGoogleSources(get().calendarSources, calendarsForMerge, selectedCalendarIds);

        // Se nessun calendario è abilitato dopo il merge, abilita quello primario
        const googleMergedSources = mergedSources.filter(s => s.type === 'google');
        if (googleMergedSources.length > 0 && !googleMergedSources.some(src => src.enabled)) {
          console.log('[fallback] No enabled Google calendars, enabling a default one');

          const primaryIndex = mergedSources.findIndex(cal =>
            cal.type === 'google' && (
              cal.name === 'Primary Calendar' ||
              cal.name.includes('primary') ||
              (cal.googleCalendarId && (cal.googleCalendarId.includes('@gmail.com') || cal.googleCalendarId.includes('@obly.it')))
            )
          );

          if (primaryIndex >= 0) {
            mergedSources[primaryIndex] = { ...mergedSources[primaryIndex], enabled: true };
            console.log(`[fallback] Enabled primary calendar: ${mergedSources[primaryIndex].name}`);
          } else {
            const firstGoogleIndex = mergedSources.findIndex(s => s.type === 'google');
            if (firstGoogleIndex >= 0) {
              mergedSources[firstGoogleIndex] = { ...mergedSources[firstGoogleIndex], enabled: true };
              console.log(`[fallback] Enabled first calendar: ${mergedSources[firstGoogleIndex].name}`);
            }
          }
        }

        set({ calendarSources: mergedSources });
        console.log('[fallback] Merged calendar sources from events');
      }
      
      // Convert Google events to our CalendarEvent format
      const googleEvents: CalendarEvent[] = data.events.map((event: GoogleEvent) => {
        const isAllDay = Boolean(!event.start.dateTime && event.start.date);

        // Get the calendar ID from the event's calendar property or fallback to organizer
        // Event's calendar property has the format 'calendarId@group.calendar.google.com'
        const calendarId = event.calendar || event.organizer?.email || '';

        // Find the matching calendar metadata
        const matchingCalendar = calendars.find((cal: any) => cal.id === calendarId);

        // Find the matching source we just created to ensure name consistency
        const matchingSource = googleSources.find(src => src.googleCalendarId === calendarId);

        return {
          id: `google-${event.id}`,
          title: event.summary,
          description: event.description || '',
          start_time: isAllDay
            ? new Date(event.start.date + 'T00:00:00')
            : new Date(event.start.dateTime || ''),
          end_time: isAllDay
            ? new Date(event.end.date + 'T23:59:59')
            : new Date(event.end.dateTime || ''),
          all_day: isAllDay,
          location: event.location || '',
          created_at: new Date().toISOString(),
          source: 'google',
          source_id: calendarId, // Store the source calendar ID
          source_name: matchingSource?.name || matchingCalendar?.summary || event.organizer?.displayName || event.organizer?.email || 'Google Calendar',
          color: event.colorId ? getGoogleEventColor(event.colorId) : (matchingSource?.color || matchingCalendar?.backgroundColor || '#039BE5'), // Use source color if available
          // google_color_id distingue il colore ESPLICITO (scelto dall'operatore)
          // dal colore di default del calendario — usato dall'overlay in getEnabledEvents.
          metadata: event.colorId ? { google_color_id: event.colorId } : undefined,
          is_editable: false
        };
      });
      
      set({ googleEvents, googleLoading: false });
      
      // Release the lock
      (useCalendarStore as any).googleFetchInProgress = false;
    } catch (error) {
      console.error('Error fetching Google events:', error);

      // Try to extract the JSON body from FunctionsHttpError
      // Supabase's FunctionsHttpError wraps the actual error in context.json()
      let errorToProcess = error;
      if (error && typeof error === 'object' && 'context' in error) {
        try {
          const errorContext = (error as any).context;
          if (errorContext && typeof errorContext.json === 'function') {
            const jsonBody = await errorContext.json();
            if (jsonBody) {
              console.log('Extracted error body from FunctionsHttpError:', jsonBody);
              errorToProcess = jsonBody;
            }
          }
        } catch (e) {
          console.error('Failed to extract error body:', e);
        }
      }

      // Parse error to get a more user-friendly message
      const parsedError = parseErrorResponse(errorToProcess);
      
      // Check for specific error categories to take appropriate action
      if (parsedError.error === ErrorCategory.AUTHENTICATION) {
        // For auth errors, suggest reconnecting Google account
        set({ 
          googleError: `Authentication error: ${parsedError.message}. Try reconnecting your Google account.`,
          googleLoading: false
        });
      } else if (parsedError.error === ErrorCategory.PERMISSION) {
        // For permission errors, suggest granting calendar access
        set({ 
          googleError: `Permission error: ${parsedError.message}. Make sure you've granted calendar access.`,
          googleLoading: false
        });
      } else if (parsedError.error === ErrorCategory.RATE_LIMIT) {
        // For rate limit errors, suggest trying again later
        set({ 
          googleError: `Rate limit exceeded: ${parsedError.message}. Please try again in a few minutes.`,
          googleLoading: false
        });
        
        // Set a longer cooldown after rate limit errors
        (useCalendarStore as any).lastGoogleFetchTime = Date.now() + 300000; // 5 minutes
      } else {
        // For other errors
        set({ 
          googleError: parsedError.message || 'Failed to fetch Google Calendar events',
          googleLoading: false
        });
      }
      
      // Always release the lock, even on error
      (useCalendarStore as any).googleFetchInProgress = false;
    }
  },

  // ============================================
  // MICROSOFT (OUTLOOK) CALENDAR — lettura eventi
  // Speculare a fetchGoogleEvents: delta sul primario, polling sui secondari.
  // ============================================
  fetchMicrosoftEvents: async (startDate, endDate, options?: { skipCalendarListFetch?: boolean; forceRefresh?: boolean }) => {
    set({ microsoftError: null });

    const skipCalendarListFetch = options?.skipCalendarListFetch || false;
    const forceRefresh = options?.forceRefresh || false;

    // Gate: servizio Calendario Outlook abilitato a livello org
    const settings = useSettingsStore.getState().settings;
    if (!(settings as any)?.microsoft_sync_calendar) {
      return;
    }

    // Lock globale (una fetch alla volta)
    if ((useCalendarStore as any).microsoftFetchInProgress) {
      return;
    }
    (useCalendarStore as any).microsoftFetchInProgress = true;

    // Cooldown speculare a Google (5s), bypassato da forceRefresh
    const now = Date.now();
    const lastFetchTime = (useCalendarStore as any).lastMicrosoftFetchTime || 0;
    const lastStart = (useCalendarStore as any).lastMicrosoftStartDate;
    const lastEnd = (useCalendarStore as any).lastMicrosoftEndDate;
    const MS_FETCH_COOLDOWN = 5000;
    const isSameRange = lastStart && lastEnd &&
      Math.abs(startDate.getTime() - lastStart.getTime()) < 86400000 * 7 &&
      Math.abs(endDate.getTime() - lastEnd.getTime()) < 86400000 * 7;
    if (!forceRefresh && isSameRange && now - lastFetchTime < MS_FETCH_COOLDOWN) {
      (useCalendarStore as any).microsoftFetchInProgress = false;
      return;
    }
    (useCalendarStore as any).lastMicrosoftFetchTime = now;
    (useCalendarStore as any).lastMicrosoftStartDate = startDate;
    (useCalendarStore as any).lastMicrosoftEndDate = endDate;

    set({ microsoftLoading: true, microsoftError: null });

    try {
      const authData = await getCachedAuthData();
      if (!authData?.userId || !authData.organizationId) {
        set({ microsoftLoading: false });
        (useCalendarStore as any).microsoftFetchInProgress = false;
        return;
      }
      if (!authData.microsoftAccountConnected) {
        // Account Microsoft non collegato: niente eventi, nessun errore rumoroso
        set({ microsoftEvents: [], microsoftLoading: false });
        (useCalendarStore as any).microsoftFetchInProgress = false;
        return;
      }

      // ===== FETCH LISTA CALENDARI (microsoft-calendar-list) =====
      // L'auth (userId/org) è ricavata dal JWT: body vuoto. Popola la sidebar.
      if (!skipCalendarListFetch) {
        try {
          const { data: listData, error: listError } = await supabase.functions.invoke('microsoft-calendar-list', { body: {} });
          if (!listError && listData?.calendars && Array.isArray(listData.calendars) && listData.calendars.length > 0) {
            const selectedIds = forceRefresh
              ? get().calendarSources.filter(s => s.type === 'microsoft' && s.enabled && s.microsoftCalendarId).map(s => s.microsoftCalendarId!)
              : (authData.microsoftCalendarIds || []);
            const calendarsForMerge = listData.calendars.map((c: any) => ({
              id: c.id,
              name: c.name || 'Outlook Calendar',
              color: getMicrosoftCalendarColor(c.color, c.hexColor),
              isDefault: c.isDefault === true,
            }));
            const merged = mergeMicrosoftSources(get().calendarSources, calendarsForMerge, selectedIds);
            set({ calendarSources: merged });
          }
        } catch (listErr) {
          console.error('[microsoft-calendar-list] error fetching list:', listErr);
        }
      }

      // Calendari da fetchare: quelli abilitati nello stato. Fallback agli id del profilo.
      let enabledCalendars = get().calendarSources.filter(s => s.type === 'microsoft' && s.enabled && s.microsoftCalendarId);
      if (enabledCalendars.length === 0) {
        const ids = authData.microsoftCalendarIds || [];
        enabledCalendars = ids.map((id: string) => ({
          id: `microsoft-${id}`,
          name: 'Outlook Calendar',
          color: '#0078D4',
          enabled: true,
          type: 'microsoft' as const,
          microsoftCalendarId: id,
          isPrimaryMicrosoft: false,
        }));
      }

      if (enabledCalendars.length === 0) {
        set({ microsoftEvents: [], microsoftLoading: false });
        (useCalendarStore as any).microsoftFetchInProgress = false;
        return;
      }

      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();

      // Working set: partiamo dallo stato corrente per applicare il delta in modo
      // incrementale sul primario (upsert + removed) senza perdere eventi non cambiati.
      const working = new Map<string, CalendarEvent>();
      for (const e of get().microsoftEvents) working.set(e.id, e);

      // Helper per applicare un batch di eventi raw a un calendario
      const applyRawEvents = (rawEvents: MicrosoftGraphEvent[], calId: string, source: CalendarSource) => {
        for (const ev of rawEvents) {
          const normalized = normalizeMicrosoftEvent(ev, calId, source);
          if (normalized) {
            working.set(normalized.id, normalized);
          } else if (ev?.id) {
            // Evento diventato cancellato/non mostrabile via delta: rimuovilo
            working.delete(`microsoft-${ev.id}`);
          }
        }
      };

      for (const cal of enabledCalendars) {
        const calId = cal.microsoftCalendarId!;
        const isPrimary = cal.isPrimaryMicrosoft === true;

        if (!isPrimary) {
          // Secondario: fetch per range visibile (niente delta) → replace di questo calendario
          const { data, error } = await supabase.functions.invoke('microsoft-calendar-events', {
            body: { calendar_id: calId, start_date: startIso, end_date: endIso },
          });
          if (error) throw error;
          for (const [id, e] of working) {
            if (e.source_id === calId) working.delete(id);
          }
          applyRawEvents(data?.events || [], calId, cal);
          continue;
        }

        // Primario: sync incrementale via delta_link persistito per-utente.
        // Il delta di /calendarView/delta è vincolato alla finestra temporale del
        // full sync iniziale: quindi lo usiamo per il polling sullo STESSO range,
        // ma su cambio range (navigazione mese) o forceRefresh rifacciamo un full
        // sync sulla nuova finestra visibile.
        const forceFullSync = forceRefresh || !isSameRange;
        let deltaLink = forceFullSync ? undefined : (loadMicrosoftDelta(authData.userId) || undefined);
        if (forceFullSync) {
          clearMicrosoftDelta(authData.userId);
        }
        let handled = false;
        let attempts = 0;
        while (!handled && attempts < 2) {
          attempts++;
          const isFullFetch = !deltaLink;
          const body: any = deltaLink
            ? { calendar_id: calId, delta_link: deltaLink }
            : { calendar_id: calId, start_date: startIso, end_date: endIso };

          const { data, error } = await supabase.functions.invoke('microsoft-calendar-events', { body });
          if (error) throw error;

          // resync_required → azzera il delta e rifai un full fetch
          if (data?.resync_required) {
            clearMicrosoftDelta(authData.userId);
            deltaLink = undefined;
            continue;
          }

          // Su full fetch (iniziale o post-resync) ricostruiamo il calendario da zero
          if (isFullFetch) {
            for (const [id, e] of working) {
              if (e.source_id === calId) working.delete(id);
            }
          }

          applyRawEvents(data?.events || [], calId, cal);

          // removed: rimuovi gli eventi cancellati (vale sia per full che incrementale)
          for (const removedId of (data?.removed || [])) {
            working.delete(`microsoft-${removedId}`);
          }

          // Persisti il nuovo delta_link per la prossima sync incrementale
          if (data?.delta_link) {
            saveMicrosoftDelta(authData.userId, data.delta_link);
          }
          handled = true;
        }
      }

      set({ microsoftEvents: Array.from(working.values()), microsoftLoading: false });
      (useCalendarStore as any).microsoftFetchInProgress = false;
    } catch (error) {
      console.error('Error fetching Microsoft events:', error);

      // Estrai il body JSON da FunctionsHttpError (come nel ramo Google)
      let errorToProcess: unknown = error;
      if (error && typeof error === 'object' && 'context' in error) {
        try {
          const ctx = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const jsonBody = await ctx.json();
            if (jsonBody) errorToProcess = jsonBody;
          }
        } catch (_e) { /* ignore */ }
      }

      const parsedError = parseErrorResponse(errorToProcess);
      set({
        microsoftError: parsedError.message || 'Failed to fetch Microsoft Calendar events',
        microsoftLoading: false,
      });
      (useCalendarStore as any).microsoftFetchInProgress = false;
    }
  },

  fetchClientEvents: async (clientId) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('client_id', clientId)
        .order('start_time', { ascending: true });

      if (error) throw error;
      set({ events: data || [], loading: false });
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ error: parsedError.message, loading: false });
    }
  },

  createEvent: async (event) => {
    try {
      const authData = await getCachedAuthData();
      if (!authData) throw new Error('User not authenticated');

      // Estrai solo le proprietà che esistono nella tabella calendar_events
      // Esclude: attendees, conference_data, save_to_google, google_calendar_id, location
      const {
        title,
        description,
        start_time,
        end_time,
        all_day,
        client_id,
        project_id,
        event_category,
        task_ids,
        metadata,
        assigned_to,
        source_id
      } = event;

      const dbEvent = {
        title,
        description,
        start_time,
        end_time,
        all_day,
        client_id,
        project_id,
        organization_id: authData.organizationId,
        event_category,
        task_ids,
        metadata,
        assigned_to,
        source_id,
        created_by: authData.userId,
      };

      // Rimuovi proprietà undefined
      const cleanEvent = Object.fromEntries(
        Object.entries(dbEvent).filter(([_, v]) => v !== undefined)
      );

      const { data, error } = await supabase
        .from('calendar_events')
        .insert(cleanEvent)
        .select()
        .single();

      if (error) throw error;

      set({ events: [...get().events, data] });

      // Bridge: create on AlfaDocs if requested
      if ((event.metadata as any)?.create_on_alfadocs && data.client_id) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', user?.id).single();
          if (profile?.organization_id) {
            // Risolve la connessione AlfaDocs del cliente (practice primaria, con
            // fallback alla più vecchia connessione attiva collegata)
            const resolved = await resolveClientAlfaConfig(data.client_id, profile.organization_id);
            if (resolved) {
              const colorId = resolved.config.configuration?.appointments_config?.default_color_id;
              if (colorId) {
                const startTime = new Date(data.start_time);
                const endTime = data.end_time ? new Date(data.end_time) : new Date(startTime.getTime() + 30 * 60000);
                const durationMin = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
                // Formato locale per AlfaDocs (no timezone)
                const pad = (n: number) => String(n).padStart(2, '0');
                const alfaDate = `${startTime.getFullYear()}-${pad(startTime.getMonth() + 1)}-${pad(startTime.getDate())} ${pad(startTime.getHours())}:${pad(startTime.getMinutes())}:${pad(startTime.getSeconds())}`;
                const alfaType = (event.metadata as any)?.alfadocs_event_type;
                const res = await AlfaDocsService.createAppointment(profile.organization_id, {
                  date: alfaDate,
                  patientId: parseInt(resolved.patientId),
                  duration: durationMin,
                  description: data.title,
                  type: alfaType === 'telehealth' ? 'telehealth' : 'inPractice',
                  colorId,
                }, { configId: resolved.config.id });
                if (res?.data?.id) {
                  const practiceId = resolved.config.configuration?.practice_id;
                  // Update calendar_events metadata
                  await supabase.from('calendar_events')
                    .update({ metadata: { ...data.metadata, alfadocs_appointment_id: String(res.data.id), alfadocs_practice_id: practiceId } })
                    .eq('id', data.id);
                  // Create sales_activity for client detail page visibility
                  await supabase.from('sales_activities').insert({
                    title: data.title,
                    description: data.description || '',
                    type: `alfadocs_${alfaType}`,
                    status: 'pending',
                    due_date: data.start_time,
                    end_date: data.end_time,
                    lead_id: data.client_id,
                    user_id: user?.id,
                    assigned_to: user?.id,
                    organization_id: profile.organization_id,
                    metadata: { alfadocs_appointment_id: String(res.data.id), alfadocs_type: alfaType, alfadocs_practice_id: practiceId },
                  });
                }
              }
            }
          }
        } catch (alfaErr) {
          console.warn('[Calendar] AlfaDocs create bridge failed:', alfaErr);
        }
      }

      return data;
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ error: parsedError.message });
      return null;
    }
  },

  updateEvent: async (id, event) => {
    try {
      // Find the event to determine its type and source
      const allEvents = [...get().events, ...get().salesActivities, ...get().projectTasks, ...get().bookingEvents, ...get().googleEvents];
      const targetEvent = allEvents.find(e => e.id === id);

      if (!targetEvent) {
        throw new Error('Event not found');
      }

      // Handle different event types
      if (targetEvent.source === 'sales_activities' || targetEvent.event_type === 'sales_activity') {
        // This is a sales activity - update the sales_activities table
        if (!targetEvent.original_id) {
          throw new Error('Sales activity original_id not found');
        }

        // Map calendar event fields to sales activity fields
        const salesActivityUpdate: any = {};

        if (event.title) {
          // Extract the original title (remove client info if present)
          const originalTitle = event.title.includes(' - ')
            ? event.title.split(' - ')[0]
            : event.title;
          salesActivityUpdate.title = originalTitle;
        }

        if (event.description) {
          // Extract original description (remove CRM info section)
          let description = event.description.includes('--- CRM Info ---')
            ? event.description.split('--- CRM Info ---')[0]
            : event.description;
          // fetchSalesActivities appende "\n\nCliente: ...\nTipo: ...\nAssegnato a: ...":
          // senza questo strip il blocco sintetico veniva riscritto nella description
          const clientInfoIdx = description.indexOf('\n\nCliente: ');
          if (clientInfoIdx !== -1) {
            description = description.slice(0, clientInfoIdx);
          }
          salesActivityUpdate.description = description.trim();
        }

        if (event.start_time) {
          salesActivityUpdate.due_date = event.start_time;
        }

        // Update sales activity in database
        const { data: updatedActivity, error: activityError } = await supabase
          .from('sales_activities')
          .update(salesActivityUpdate)
          .eq('id', targetEvent.original_id)
          .select()
          .single();

        if (activityError) throw activityError;

        // Update the event in the local state
        const updatedEvent = { ...targetEvent, ...event };

        set({
          salesActivities: get().salesActivities.map(e => (e.id === id ? updatedEvent : e)),
        });

        return updatedEvent;
      }
      else if (targetEvent.source === 'project_tasks' || targetEvent.event_type === 'project_task') {
        // Handle project tasks if needed
        throw new Error('Project task updates not implemented yet');
      }
      else if (targetEvent.source === 'google') {
        // Handle Google Calendar events
        throw new Error('Google Calendar event updates should use updateGoogleEvent');
      }
      else {
        // Default: regular calendar event
        // Estrai solo le proprietà che esistono nella tabella calendar_events
        const {
          title,
          description,
          start_time,
          end_time,
          all_day,
          client_id,
          project_id,
          organization_id,
          event_category,
          task_ids,
          metadata,
          assigned_to,
          source_id
        } = event;

        const dbEvent = {
          title,
          description,
          start_time,
          end_time,
          all_day,
          client_id,
          project_id,
          organization_id,
          event_category,
          task_ids,
          metadata,
          assigned_to,
          source_id
        };

        // Rimuovi proprietà undefined
        const cleanEvent = Object.fromEntries(
          Object.entries(dbEvent).filter(([_, v]) => v !== undefined)
        );

        const { data, error } = await supabase
          .from('calendar_events')
          .update(cleanEvent)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;

        set({
          events: get().events.map(e => (e.id === id ? data : e)),
        });

        // Bridge: propaga update su AlfaDocs se collegato
        const alfaId = (data.metadata as any)?.alfadocs_appointment_id || (targetEvent.metadata as any)?.alfadocs_appointment_id;
        if (alfaId && data.organization_id) {
          try {
            const updateFields: Record<string, any> = {};
            if (event.start_time) {
              // Formato locale per AlfaDocs (no timezone)
              const pad = (n: number) => String(n).padStart(2, '0');
              const st = new Date(event.start_time);
              updateFields.date = `${st.getFullYear()}-${pad(st.getMonth() + 1)}-${pad(st.getDate())} ${pad(st.getHours())}:${pad(st.getMinutes())}:${pad(st.getSeconds())}`;
            }
            if (event.start_time && event.end_time) {
              const dur = Math.round((new Date(event.end_time).getTime() - new Date(event.start_time).getTime()) / 60000);
              if (dur > 0) updateFields.duration = dur;
            }
            if (event.title) updateFields.description = event.title;
            if (Object.keys(updateFields).length > 0) {
              // Risolve il configId dallo studio collegato all'appuntamento
              const configs = await AlfaDocsService.listConfigs(data.organization_id);
              const configId = resolveEventAlfaConfigId(
                { metadata: data.metadata ?? targetEvent.metadata, source_id: data.source_id ?? targetEvent.source_id },
                configs
              );
              AlfaDocsService.updateAppointment(data.organization_id, alfaId, updateFields, { configId }).catch(err =>
                console.warn('[Calendar] AlfaDocs update bridge failed:', err)
              );
            }
          } catch (e) { console.warn('[Calendar] AlfaDocs bridge error:', e); }
        }

        // Bridge: propaga update su Google Calendar se collegato
        const googleEventId = (data.metadata as any)?.google_event_id;
        const googleCalId = (data.metadata as any)?.google_calendar_id;
        if (googleEventId && googleCalId) {
          get().updateGoogleEvent(googleEventId, googleCalId, event).catch(err =>
            console.warn('[Calendar] Google Calendar update bridge failed:', err)
          );
        }

        return data;
      }
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ error: parsedError.message });
      return null;
    }
  },

  deleteEvent: async (id) => {
    try {
      // Find the event to determine its type and source
      const allEvents = [...get().events, ...get().salesActivities, ...get().projectTasks, ...get().bookingEvents, ...get().googleEvents, ...get().alfadocsEvents];
      const targetEvent = allEvents.find(e => e.id === id);

      if (!targetEvent) {
        throw new Error('Event not found');
      }

      // Handle different event types
      if (targetEvent.source === 'sales_activities' || targetEvent.event_type === 'sales_activity') {
        // This is a sales activity - delete from sales_activities table
        if (!targetEvent.original_id) {
          throw new Error('Sales activity original_id not found');
        }

        // Bridge delete evento Google collegato: senza questo, cancellare
        // l'attività dal Calendario lasciava l'evento orfano su Google
        // (i drawer lead/deal lo facevano già, questo ramo no)
        const { data: activityRow } = await supabase
          .from('sales_activities')
          .select('google_event_id, calendar_id')
          .eq('id', targetEvent.original_id)
          .maybeSingle();

        const { error: activityError } = await supabase
          .from('sales_activities')
          .delete()
          .eq('id', targetEvent.original_id);

        if (activityError) throw activityError;

        if (activityRow?.google_event_id && activityRow?.calendar_id) {
          try {
            await get().deleteGoogleEvent(activityRow.google_event_id, activityRow.calendar_id);
          } catch (gErr) {
            console.warn('[Calendar] Google delete bridge failed:', gErr);
          }
        }

        // Cancel on AlfaDocs if linked
        const alfaApptId = (targetEvent.metadata as any)?.alfadocs_appointment_id;
        if (alfaApptId) {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', user?.id).single();
            if (profile?.organization_id) {
              const configs = await AlfaDocsService.listConfigs(profile.organization_id);
              const configId = resolveEventAlfaConfigId(targetEvent, configs);
              await AlfaDocsService.updateAppointment(profile.organization_id, alfaApptId, { state: 'cancelled' }, { configId });
            }
          } catch (alfaErr) {
            console.warn('[Calendar] AlfaDocs cancel bridge failed:', alfaErr);
          }
        }

        // Update local state
        set({
          salesActivities: get().salesActivities.filter(e => e.id !== id)
        });

        return true;
      }
      else if (targetEvent.source === 'alfadocs') {
        // AlfaDocs event - cancel on AlfaDocs and update visual state (don't remove)
        const parsedAlfaSource = parseAlfaSourceId(targetEvent.source_id);
        const alfaEventId = targetEvent.original_id || parsedAlfaSource?.appointmentId;
        if (alfaEventId) {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', user?.id).single();
            if (profile?.organization_id) {
              const configs = await AlfaDocsService.listConfigs(profile.organization_id);
              const configId = resolveEventAlfaConfigId(targetEvent, configs);
              await AlfaDocsService.updateAppointment(profile.organization_id, alfaEventId, { state: 'cancelled' }, { configId });
            }
          } catch (alfaErr) {
            console.warn('[Calendar] AlfaDocs cancel failed:', alfaErr);
          }
        }
        // Update local state to show cancelled status (don't remove)
        set({
          alfadocsEvents: get().alfadocsEvents.map(e =>
            e.id === id
              ? { ...e, metadata: { ...e.metadata, alfadocs_state: 'cancelled' }, color: '#9CA3AF' }
              : e
          )
        });
        return true;
      }
      else if (targetEvent.source === 'project_tasks' || targetEvent.event_type === 'project_task') {
        // Handle project tasks if needed
        throw new Error('Project task deletion not implemented yet');
      }
      else if (targetEvent.source === 'google') {
        // Handle Google Calendar events
        throw new Error('Google Calendar event deletion should use deleteGoogleEvent');
      }
      else {
        // Default: regular calendar event
        const { error } = await supabase
          .from('calendar_events')
          .delete()
          .eq('id', id);

        if (error) throw error;

        set({ events: get().events.filter(e => e.id !== id) });

        // Bridge: cancella su AlfaDocs se collegato
        const alfaId = (targetEvent.metadata as any)?.alfadocs_appointment_id;
        const orgId = (targetEvent as any).organization_id;
        if (alfaId && orgId) {
          AlfaDocsService.listConfigs(orgId)
            .then(configs => {
              const configId = resolveEventAlfaConfigId(targetEvent, configs);
              return AlfaDocsService.updateAppointment(orgId, alfaId, { state: 'cancelled' }, { configId });
            })
            .catch(err => console.warn('[Calendar] AlfaDocs cancel bridge failed:', err));
        }

        // Bridge: cancella su Google Calendar se collegato
        const googleEventId = (targetEvent.metadata as any)?.google_event_id;
        const googleCalId = (targetEvent.metadata as any)?.google_calendar_id;
        if (googleEventId && googleCalId) {
          get().deleteGoogleEvent(googleEventId, googleCalId).catch(err =>
            console.warn('[Calendar] Google Calendar delete bridge failed:', err)
          );
        }

        return true;
      }
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ error: parsedError.message });
      return false;
    }
  },

  // GOOGLE CALENDAR BIDIRECTIONAL SYNC METHODS

  // Create a new event in Google Calendar
  createGoogleEvent: async (calendarId, event, assignedUserId) => {
    try {
      set({ googleLoading: true, googleError: null });

      // Get user profile to check organization_id
      const { data: userData } = await supabase.auth.getUser();

      if (!userData?.user?.id) {
        throw new Error("Not authenticated");
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error("User not associated with an organization");
      }

      // Create the event via our Edge Function with retry logic
      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('google-calendar-modify', {
          body: {
            operation: 'create',
            organization_id: userProfile.organization_id,
            calendar_id: calendarId,
            assigned_user_id: assignedUserId, // Pass the assigned user ID
            event: {
              title: event.title,
              description: event.description || '',
              start_time: event.start_time,
              end_time: event.end_time,
              all_day: event.all_day || false,
              location: event.location || '',
              color: event.color || '#039BE5',
              attendees: event.attendees || [],
              reminders: event.reminders || [],
              conference_data: event.conference_data // Pass the conference data if provided
            }
          }
        });
        
        if (response.error) {
          throw response.error;
        }
        
        return response;
      }, {
        maxRetries: 2,
        initialDelay: 1000
      });
      
      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to create Google Calendar event');
      }
      
      // Convert Google event response to our format
      if (data && data.event) {
        const googleEvent = data.event;
        const isAllDay = Boolean(!googleEvent.start.dateTime && googleEvent.start.date);
        
        // Extract conference data from the response if available
        let conferenceData = undefined;
        if (googleEvent.conferenceData) {
          conferenceData = {
            conferenceId: googleEvent.conferenceData.conferenceId,
            entryPoints: googleEvent.conferenceData.entryPoints,
            // Don't include createRequest in the saved data, it's only for creation
          };
        }
        
        const newEvent: CalendarEvent = {
          id: `google-${googleEvent.id}`,
          title: googleEvent.summary,
          description: googleEvent.description || '',
          start_time: isAllDay 
            ? new Date(googleEvent.start.date + 'T00:00:00') 
            : new Date(googleEvent.start.dateTime || ''),
          end_time: isAllDay 
            ? new Date(googleEvent.end.date + 'T23:59:59') 
            : new Date(googleEvent.end.dateTime || ''),
          all_day: isAllDay,
          location: googleEvent.location || '',
          created_at: new Date().toISOString(),
          source: 'google',
          source_id: calendarId,
          color: googleEvent.colorId ? getGoogleEventColor(googleEvent.colorId) : '#039BE5',
          metadata: googleEvent.colorId || data.invite_not_sent
            ? {
                ...(googleEvent.colorId ? { google_color_id: googleEvent.colorId } : {}),
                // Limite inviti dell'account Google: evento salvato senza email agli invitati
                ...(data.invite_not_sent ? { invite_not_sent: true } : {}),
              }
            : undefined,
          is_editable: true,
          conference_data: conferenceData
        };
        
        set({ 
          googleEvents: [...get().googleEvents, newEvent],
          googleLoading: false 
        });
        
        return newEvent;
      }
      
      set({ googleLoading: false });
      return null;
    } catch (error) {
      console.error('Error creating Google Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      set({ 
        googleError: parsedError.message || 'Failed to create Google Calendar event',
        googleLoading: false 
      });
      return null;
    }
  },

  // Update an existing Google Calendar event
  updateGoogleEvent: async (eventId, calendarId, event) => {
    try {
      set({ googleLoading: true, googleError: null });
      
      // Get user profile to check organization_id
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.id) {
        throw new Error("Not authenticated");
      }
      
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
        
      if (!userProfile?.organization_id) {
        throw new Error("User not associated with an organization");
      }
      
      // Extract the Google event ID if it has the 'google-' prefix
      const googleEventId = eventId.startsWith('google-') ? eventId.substring(7) : eventId;

      // L'evento potrebbe non essere nel local state (es. modifica da LeadStatusUpdateDrawer)
      // Cerchiamo nel local state ma non falliamo se non lo troviamo
      const existingEvent = get().googleEvents.find(e => e.id === eventId);

      // Merge con existingEvent se disponibile, altrimenti usa solo i dati passati
      const updatedEventData = existingEvent
        ? {
            ...existingEvent,
            ...event,
            // Ensure these fields use our format
            title: event.title || existingEvent.title,
            description: event.description !== undefined ? event.description : existingEvent.description,
            start_time: event.start_time || existingEvent.start_time,
            end_time: event.end_time || existingEvent.end_time,
            all_day: event.all_day !== undefined ? event.all_day : existingEvent.all_day,
            location: event.location !== undefined ? event.location : existingEvent.location,
            color: event.color || existingEvent.color
          }
        : {
            title: event.title || '',
            description: event.description || '',
            start_time: event.start_time || new Date(),
            end_time: event.end_time || new Date(),
            all_day: event.all_day || false,
            location: event.location || '',
            color: event.color || '#039BE5',
            attendees: event.attendees || []
          };
      
      // Update the event via our Edge Function with retry logic
      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('google-calendar-modify', {
          body: {
            operation: 'update',
            organization_id: userProfile.organization_id,
            calendar_id: calendarId,
            event_id: googleEventId,
            event: {
              title: updatedEventData.title,
              description: updatedEventData.description || '',
              start_time: updatedEventData.start_time,
              end_time: updatedEventData.end_time,
              all_day: updatedEventData.all_day || false,
              location: updatedEventData.location || '',
              color: updatedEventData.color || '#039BE5',
              attendees: updatedEventData.attendees || [],
              reminders: (updatedEventData as { reminders?: unknown[] }).reminders || []
            }
          }
        });
        
        if (response.error) {
          throw response.error;
        }
        
        return response;
      }, {
        maxRetries: 2,
        initialDelay: 1000
      });
      
      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to update Google Calendar event');
      }
      
      // Convert Google event response to our format
      if (data && data.event) {
        const googleEvent = data.event;
        const isAllDay = Boolean(!googleEvent.start.dateTime && googleEvent.start.date);
        
        const updatedEvent: CalendarEvent = {
          id: eventId, // Keep original ID formatting
          title: googleEvent.summary,
          description: googleEvent.description || '',
          start_time: isAllDay
            ? new Date(googleEvent.start.date + 'T00:00:00')
            : new Date(googleEvent.start.dateTime || ''),
          end_time: isAllDay
            ? new Date(googleEvent.end.date + 'T23:59:59')
            : new Date(googleEvent.end.dateTime || ''),
          all_day: isAllDay,
          location: googleEvent.location || '',
          created_at: existingEvent?.created_at || new Date().toISOString(),
          source: 'google',
          source_id: calendarId,
          color: googleEvent.colorId ? getGoogleEventColor(googleEvent.colorId) : '#039BE5',
          metadata: googleEvent.colorId ? { google_color_id: googleEvent.colorId } : undefined,
          is_editable: true
        };

        // Update or add the event in our local state
        set({
          googleEvents: existingEvent
            ? get().googleEvents.map(e => e.id === eventId ? updatedEvent : e)
            : [...get().googleEvents, updatedEvent],
          googleLoading: false
        });
        
        return updatedEvent;
      }
      
      set({ googleLoading: false });
      return null;
    } catch (error) {
      console.error('Error updating Google Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      set({ 
        googleError: parsedError.message || 'Failed to update Google Calendar event',
        googleLoading: false 
      });
      return null;
    }
  },

  // Delete a Google Calendar event
  deleteGoogleEvent: async (eventId, calendarId) => {
    try {
      set({ googleLoading: true, googleError: null });

      // Get user profile to check organization_id
      const { data: userData } = await supabase.auth.getUser();

      if (!userData?.user?.id) {
        throw new Error("Not authenticated");
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error("User not associated with an organization");
      }

      // Extract the Google event ID if it has the 'google-' prefix
      const googleEventId = eventId.startsWith('google-') ? eventId.substring(7) : eventId;

      // Delete the event via our Edge Function with retry logic
      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('google-calendar-modify', {
          body: {
            operation: 'delete',
            organization_id: userProfile.organization_id,
            calendar_id: calendarId,
            event_id: googleEventId
          }
        });

        if (response.error) {
          // Special case for 404 - if event is already deleted, that's OK
          const parsedError = parseErrorResponse(response.error);
          if (parsedError.status === 404 ||
              parsedError.message?.includes('not found') ||
              parsedError.message?.includes('Not Found') ||
              parsedError.message?.includes('404')) {
            return { data: { success: true, operation: 'delete', event_id: eventId, already_deleted: true }, error: null };
          }

          throw response.error;
        }

        return response;
      }, {
        maxRetries: 2,
        initialDelay: 1000
      });

      // Check for errors, but handle 404 as success
      if (error) {
        const parsedError = parseErrorResponse(error);

        // Treat 404 as success (event already deleted)
        if (parsedError.status === 404 ||
            parsedError.message?.includes('not found') ||
            parsedError.message?.includes('Not Found') ||
            parsedError.message?.includes('404')) {
          console.log('[calendarStore] Event not found (404) - treating as success');
          // Remove from local state and return success
          set({
            googleEvents: get().googleEvents.filter(e => e.id !== eventId),
            googleLoading: false
          });
          return true;
        }

        throw new Error(parsedError.message || 'Failed to delete Google Calendar event');
      }

      // Remove the event from our local state
      set({
        googleEvents: get().googleEvents.filter(e => e.id !== eventId),
        googleLoading: false
      });

      return true;
    } catch (error) {
      console.error('Error deleting Google Calendar event:', error);
      const parsedError = parseErrorResponse(error);

      set({
        googleError: parsedError.message || 'Failed to delete Google Calendar event',
        googleLoading: false
      });
      return false;
    }
  },

  // Get a single Google Calendar event with full details (including attendees)
  getGoogleEvent: async (eventId, calendarId) => {
    try {
      set({ googleLoading: true, googleError: null });

      // Get user profile to check organization_id
      const { data: userData } = await supabase.auth.getUser();

      if (!userData?.user?.id) {
        throw new Error("Not authenticated");
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error("User not associated with an organization");
      }

      // Extract the Google event ID if it has the 'google-' prefix
      const googleEventId = eventId.startsWith('google-') ? eventId.substring(7) : eventId;

      console.log('[calendarStore] DEBUG - getGoogleEvent parameters:', {
        originalEventId: eventId,
        hasGooglePrefix: eventId.startsWith('google-'),
        googleEventId: googleEventId,
        calendarId: calendarId,
        organizationId: userProfile.organization_id,
        eventIdHasAt: googleEventId.includes('@'),
        eventIdBeforeAt: googleEventId.includes('@') ? googleEventId.split('@')[0] : googleEventId
      });

      // Get the event via our Edge Function with retry logic
      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('google-calendar-modify', {
          body: {
            operation: 'get',
            organization_id: userProfile.organization_id,
            calendar_id: calendarId,
            event_id: googleEventId
          }
        });

        console.log('[calendarStore] DEBUG - Edge Function response:', {
          hasData: !!response.data,
          hasError: !!response.error,
          eventReturned: response.data?.event ? 'yes' : 'no',
          errorMessage: response.error?.message
        });

        if (response.error) {
          throw response.error;
        }

        return response;
      }, {
        maxRetries: 2,
        initialDelay: 1000
      });

      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to get Google Calendar event');
      }

      set({ googleLoading: false });

      // Return the full event data including attendees
      return data?.event || null;
    } catch (error) {
      console.error('Error getting Google Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      set({
        googleError: parsedError.message || 'Failed to get Google Calendar event',
        googleLoading: false
      });
      return null;
    }
  },

  // ============================================
  // MICROSOFT (OUTLOOK) CALENDAR — bidirectional sync (create/update/delete/get)
  // Speculari ai metodi Google. L'auth (userId/org) è ricavata dal JWT nella edge:
  // NON si passa user_id/organization_id nel body (diverso dal ramo Google).
  // ============================================

  // Crea un evento nel calendario Outlook
  createMicrosoftEvent: async (calendarId, event, options) => {
    try {
      set({ microsoftLoading: true, microsoftError: null });

      const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const pad = (n: number) => String(n).padStart(2, '0');
      const toGraphDateTime = (d: Date | string) => {
        const dt = typeof d === 'string' ? new Date(d) : d;
        return {
          dateTime: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`,
          timeZone: localTz,
        };
      };

      const graphEvent: any = {
        subject: event.title,
        description: event.description || '',
        start: toGraphDateTime(event.start_time),
        end: toGraphDateTime(event.end_time),
        isAllDay: event.all_day || false,
      };
      if (event.location) graphEvent.location = { displayName: event.location };
      if (event.attendees && event.attendees.length > 0) {
        graphEvent.attendees = event.attendees.map((email: string) => ({
          emailAddress: { address: email },
          type: 'required',
        }));
      }

      // transactionId univoco per submission (non deterministico): evita collisioni Graph
      // quando la stessa activity viene ricreata più volte sullo stesso mailbox (riassegnazione,
      // disable/enable sync). L'edge function priorizza sempre event.transactionId se presente.
      // Costruito UNA volta fuori da withRetry così i retry HTTP interni riusano lo stesso valore
      // (idempotenza sui retry preservata).
      graphEvent.transactionId = options?.activityId
        ? `flusia-${options.activityId}-${crypto.randomUUID()}`
        : `flusia-${crypto.randomUUID()}`;

      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('microsoft-calendar-modify', {
          body: {
            operation: 'create',
            calendar_id: calendarId,
            event: graphEvent,
            create_teams_meeting: options?.createTeamsMeeting === true,
            activity_id: options?.activityId,
            ...(options?.targetUserId ? { target_user_id: options.targetUserId } : {}),
          },
        });
        if (response.error) throw response.error;
        return response;
      }, { maxRetries: 2, initialDelay: 1000 });

      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to create Microsoft Calendar event');
      }

      const eventId: string = data?.event_id || data?.event?.id;
      const joinUrl: string | undefined = data?.join_url || data?.event?.onlineMeeting?.joinUrl;

      const source = get().calendarSources.find(s => s.type === 'microsoft' && s.microsoftCalendarId === calendarId);
      const normalized = data?.event
        ? normalizeMicrosoftEvent(data.event as MicrosoftGraphEvent, calendarId || (source?.microsoftCalendarId ?? ''), source)
        : null;

      if (normalized) {
        set({ microsoftEvents: [...get().microsoftEvents, normalized], microsoftLoading: false });
      } else {
        set({ microsoftLoading: false });
      }

      return { event: normalized as CalendarEvent, eventId, joinUrl };
    } catch (error) {
      console.error('Error creating Microsoft Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      const msg = await resolveMicrosoftTargetErrorMessage(error, parsedError.message || 'Failed to create Microsoft Calendar event');
      set({ microsoftError: msg, microsoftLoading: false });
      return null;
    }
  },

  // Aggiorna un evento Outlook esistente
  updateMicrosoftEvent: async (eventId, event, targetUserId) => {
    try {
      set({ microsoftLoading: true, microsoftError: null });

      const microsoftEventId = eventId.startsWith('microsoft-') ? eventId.substring(10) : eventId;

      const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const pad = (n: number) => String(n).padStart(2, '0');
      const toGraphDateTime = (d: Date | string) => {
        const dt = typeof d === 'string' ? new Date(d) : d;
        return {
          dateTime: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`,
          timeZone: localTz,
        };
      };

      // Solo i campi presenti in `event` (PATCH parziale — l'attivazione Teams è irreversibile)
      const graphEvent: any = {};
      if (event.title !== undefined) graphEvent.subject = event.title;
      if (event.description !== undefined) graphEvent.description = event.description || '';
      if (event.start_time !== undefined) graphEvent.start = toGraphDateTime(event.start_time);
      if (event.end_time !== undefined) graphEvent.end = toGraphDateTime(event.end_time);
      if (event.all_day !== undefined) graphEvent.isAllDay = event.all_day;
      if (event.location !== undefined) graphEvent.location = { displayName: event.location || '' };

      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('microsoft-calendar-modify', {
          body: { operation: 'update', event_id: microsoftEventId, event: graphEvent, ...(targetUserId ? { target_user_id: targetUserId } : {}) },
        });
        if (response.error) throw response.error;
        return response;
      }, { maxRetries: 2, initialDelay: 1000 });

      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to update Microsoft Calendar event');
      }

      // not_found: prosegui senza errore (evento eliminato lato Outlook)
      if (data?.not_found) {
        set({ microsoftEvents: get().microsoftEvents.filter(e => e.id !== eventId), microsoftLoading: false });
        return null;
      }

      const existing = get().microsoftEvents.find(e => e.id === eventId);
      const calId = existing?.source_id || '';
      const source = get().calendarSources.find(s => s.type === 'microsoft' && s.microsoftCalendarId === calId);
      const normalized = data?.event
        ? normalizeMicrosoftEvent(data.event as MicrosoftGraphEvent, calId, source)
        : null;

      if (normalized) {
        set({
          microsoftEvents: existing
            ? get().microsoftEvents.map(e => (e.id === eventId ? normalized : e))
            : [...get().microsoftEvents, normalized],
          microsoftLoading: false,
        });
        return normalized;
      }

      set({ microsoftLoading: false });
      return null;
    } catch (error) {
      console.error('Error updating Microsoft Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      const msg = await resolveMicrosoftTargetErrorMessage(error, parsedError.message || 'Failed to update Microsoft Calendar event');
      set({ microsoftError: msg, microsoftLoading: false });
      return null;
    }
  },

  // Elimina un evento Outlook
  deleteMicrosoftEvent: async (eventId, targetUserId) => {
    try {
      set({ microsoftLoading: true, microsoftError: null });
      const microsoftEventId = eventId.startsWith('microsoft-') ? eventId.substring(10) : eventId;

      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('microsoft-calendar-modify', {
          body: { operation: 'delete', event_id: microsoftEventId, ...(targetUserId ? { target_user_id: targetUserId } : {}) },
        });
        if (response.error) throw response.error;
        return response;
      }, { maxRetries: 2, initialDelay: 1000 });

      // delete/not_found → prosegui senza errore
      if (error) {
        const parsedError = parseErrorResponse(error);
        if (parsedError.status === 404 || parsedError.message?.toLowerCase().includes('not found')) {
          set({ microsoftEvents: get().microsoftEvents.filter(e => e.id !== eventId), microsoftLoading: false });
          return true;
        }
        throw new Error(parsedError.message || 'Failed to delete Microsoft Calendar event');
      }

      // data?.success === true oppure data?.not_found === true: entrambi rimuovono lo stato locale
      set({ microsoftEvents: get().microsoftEvents.filter(e => e.id !== eventId), microsoftLoading: false });
      return true;
    } catch (error) {
      console.error('Error deleting Microsoft Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      const msg = await resolveMicrosoftTargetErrorMessage(error, parsedError.message || 'Failed to delete Microsoft Calendar event');
      set({ microsoftError: msg, microsoftLoading: false });
      return false;
    }
  },

  // Legge un singolo evento Outlook (dettaglio completo)
  getMicrosoftEvent: async (eventId) => {
    try {
      set({ microsoftLoading: true, microsoftError: null });
      const microsoftEventId = eventId.startsWith('microsoft-') ? eventId.substring(10) : eventId;

      const { data, error } = await withRetry(async () => {
        const response = await supabase.functions.invoke('microsoft-calendar-modify', {
          body: { operation: 'get', event_id: microsoftEventId },
        });
        if (response.error) throw response.error;
        return response;
      }, { maxRetries: 2, initialDelay: 1000 });

      if (error) {
        const parsedError = parseErrorResponse(error);
        throw new Error(parsedError.message || 'Failed to get Microsoft Calendar event');
      }

      set({ microsoftLoading: false });
      if (data?.not_found) return null;
      return data?.event || null;
    } catch (error) {
      console.error('Error getting Microsoft Calendar event:', error);
      const parsedError = parseErrorResponse(error);
      set({ microsoftError: parsedError.message || 'Failed to get Microsoft Calendar event', microsoftLoading: false });
      return null;
    }
  },

  // Search events by keyword in title or description
  searchEvents: (query) => {
    const allEvents = [...get().events, ...get().googleEvents, ...get().bookingEvents];
    const searchTerm = query.toLowerCase().trim();
    
    // If no search term, return all events
    if (!searchTerm) return allEvents;
    
    return allEvents.filter(event => {
      return (
        (event.title && event.title.toLowerCase().includes(searchTerm)) ||
        (event.description && event.description.toLowerCase().includes(searchTerm)) ||
        (event.location && event.location.toLowerCase().includes(searchTerm))
      );
    });
  },

  // Get sync token for incremental sync
  getSyncToken: () => {
    return get()._syncToken;
  },

  // Save and use sync token for incremental sync
  useSyncToken: (token) => {
    localStorage.setItem('google_calendar_sync_token', token);
    set({ _syncToken: token });
  },
  
  // Clear sync token to force full refresh
  clearSyncToken: () => {
    localStorage.removeItem('google_calendar_sync_token');
    set({ _syncToken: null });
  },

  // Reset all error states
  resetErrors: () => {
    set({
      error: null,
      googleError: null,
      microsoftError: null,
      salesActivitiesError: null,
      projectTasksError: null,
      bookingEventsError: null,
      workSlotsError: null
    });
  },

  // Fetch Booking Events as calendar events
  fetchBookingEvents: async (startDate, endDate, filterByUserId?: string) => {
    set({ bookingEventsLoading: true, bookingEventsError: null });

    try {
      const authData = await getCachedAuthData();
      if (!authData) throw new Error('User not authenticated');

      let query = supabase
        .from('bookings')
        .select(`
          *,
          event_type:booking_event_types(title, color, duration_minutes, slug),
          assigned_user:user_profiles!bookings_assigned_to_fkey(full_name, email)
        `)
        .eq('organization_id', authData.organizationId)
        .gte('start_time', startDate.toISOString())
        .lte('start_time', endDate.toISOString())
        .order('start_time', { ascending: true });

      // Se filter_by_user è attivo, mostra solo le prenotazioni assegnate all'utente
      if (filterByUserId) {
        query = query.eq('assigned_to', filterByUserId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const bookingCalendarEvents: CalendarEvent[] = (data || []).map(booking => ({
        id: `booking_${booking.id}`,
        title: `${booking.event_type?.title || 'Prenotazione'} - ${booking.booker_name}`,
        description: `Prenotazione${booking.booker_email ? `\nEmail: ${booking.booker_email}` : ''}${booking.booker_phone ? `\nTel: ${booking.booker_phone}` : ''}${booking.booker_notes ? `\nNote: ${booking.booker_notes}` : ''}${booking.meet_link ? `\nMeet: ${booking.meet_link}` : ''}`,
        start_time: booking.start_time,
        end_time: booking.end_time,
        all_day: false,
        source: 'bookings',
        color: booking.event_type?.color || ({ confirmed: '#10B981', pending: '#F59E0B', cancelled: '#EF4444', completed: '#3B82F6', no_show: '#6B7280', rescheduled: '#8B5CF6' } as Record<string, string>)[booking.status] || '#F59E0B',
        original_id: booking.id,
        event_type: 'booking',
        is_editable: false,
        metadata: {
          booking_id: booking.id,
          event_type_id: booking.event_type_id,
          status: booking.status,
          booker_name: booking.booker_name,
          booker_email: booking.booker_email,
          booker_phone: booking.booker_phone,
          meet_link: booking.meet_link,
          event_type_title: booking.event_type?.title,
          assigned_to: booking.assigned_to,
          assigned_user_name: booking.assigned_user?.full_name,
          google_event_id: booking.google_event_id,
        }
      }));

      set({
        bookingEvents: bookingCalendarEvents,
        bookingEventsLoading: false
      });

    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        bookingEventsError: parsedError.message,
        bookingEventsLoading: false
      });
    }
  },

  // Fetch AlfaDocs appointments as calendar events (real-time from API)
  fetchAlfaDocsAppointments: async (startDate, endDate) => {
    const { calendarSources } = get();
    const alfaEnabled = calendarSources.some(s => s.type === 'alfadocs' && s.enabled);
    if (!alfaEnabled) {
      set({ alfadocsEvents: [] });
      return;
    }

    set({ alfadocsLoading: true });
    try {
      const authData = await getCachedAuthData();
      if (!authData?.organizationId) {
        set({ alfadocsEvents: [], alfadocsLoading: false });
        return;
      }

      // Connessioni AlfaDocs attive dell'org (multi-studio)
      const configs = (await AlfaDocsService.listConfigs(authData.organizationId)).filter(c => c.is_active);
      if (configs.length === 0) {
        set({ alfadocsEvents: [], alfadocsLoading: false, alfadocsOldestPracticeId: null });
        return;
      }
      const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
      set({ alfadocsOldestPracticeId: oldestPracticeId });

      // Fetch appuntamenti in parallelo per ogni connessione, taggando ogni
      // appuntamento con la practice/config di provenienza.
      const perConfig = await Promise.all(configs.map(async (cfg) => {
        const { data: result, error } = await supabase.functions.invoke('alfadocs-proxy', {
          body: {
            action: 'appointments',
            params: {
              dateStart: startDate.toISOString().split('T')[0],
              dateEnd: endDate.toISOString().split('T')[0],
            },
            configId: cfg.id,
          },
        });
        if (error || !result?.success) {
          console.warn('[Calendar] AlfaDocs appointments fetch failed:', cfg.display_name, error || result?.error);
          return [];
        }
        return (result.data || []).map((apt: any) => ({
          ...apt,
          _practiceId: cfg.configuration?.practice_id ?? null,
          _practiceName: cfg.display_name,
          _configId: cfg.id,
        }));
      }));
      // Tipizzato `any` di proposito: preserva il comportamento pre-esistente in
      // questo file (result.data non tipizzato da supabase.functions.invoke) di
      // non forzare il type-check strutturale sull'oggetto mappato più sotto.
      const appointments: any = perConfig.flat();

      // Build (practiceId:patientId) → client mapping (query unica sui clienti AlfaDocs dell'org)
      const patientToClient = new Map<string, { id: string; name: string }>();

      if (appointments.length > 0) {
        // Query clients that have alfadocs_links/alfadocs_patient_id in metadata OR match by lead_source
        const { data: clients } = await supabase
          .from('clients')
          .select('id, business_name, metadata')
          .eq('organization_id', authData.organizationId)
          .eq('lead_source', 'alfadocs');

        (clients || []).forEach((c: any) => {
          const links = getAlfaDocsLinks(c.metadata, oldestPracticeId);
          Object.entries(links).forEach(([practiceId, link]) => {
            if (link?.patient_id) {
              patientToClient.set(`${practiceId}:${link.patient_id}`, { id: c.id, name: c.business_name });
            }
          });
        });

        // Fallback: match by business_name for clients without metadata links
        const nameMap = new Map((clients || []).map((c: any) => [c.business_name?.toLowerCase()?.trim(), { id: c.id, name: c.business_name }]));
        appointments.forEach((apt: any) => {
          const key = `${apt._practiceId}:${apt.patientId}`;
          if (!patientToClient.has(key)) {
            const fullName = [apt.patientFirstName || apt.firstName, apt.patientLastName || apt.lastName].filter(Boolean).join(' ').toLowerCase().trim();
            const match = nameMap.get(fullName);
            if (match) patientToClient.set(key, match);
          }
        });
      }

      // Map alfadocs appointments to CalendarEvent format
      const alfaEvents: CalendarEvent[] = appointments.map((apt: any) => {
        const startTime = new Date(apt.date);
        const duration = apt.duration || 30;
        const endTime = new Date(startTime.getTime() + duration * 60000);
        const client = patientToClient.get(`${apt._practiceId}:${apt.patientId}`);
        const clientName = client?.name || '';
        // Title: client name (if found), then description if different
        const titleParts = [clientName, apt.description].filter(Boolean);
        const title = titleParts.length > 0 ? titleParts.join(' — ') : 'Appuntamento AlfaDocs';
        // Description: stato + tipo + descrizione (+ studio, se multi-connessione)
        const descParts = [
          clientName,
          apt.description,
          apt.state ? `Stato: ${apt.state}` : null,
          apt.type === 'telehealth' ? 'Telehealth' : apt.type ? 'In studio' : null,
          configs.length > 1 ? apt._practiceName : null,
        ].filter(Boolean);

        return {
          id: `alfadocs-live-${apt._practiceId ?? 'legacy'}-${apt.id}`,
          title,
          description: descParts.join(' — '),
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          all_day: apt.allDay || false,
          client_id: client?.id || undefined,
          source: 'alfadocs' as const,
          source_id: buildAlfaSourceId(apt._practiceId ?? oldestPracticeId ?? '', apt.id),
          color: '#2383E2',
          is_editable: false,
          created_at: new Date().toISOString(),
          event_category: 'meeting' as const,
          metadata: {
            alfadocs_appointment_id: String(apt.id),
            alfadocs_state: apt.state,
            alfadocs_patient_id: apt.patientId ? String(apt.patientId) : undefined,
            alfadocs_operator_id: apt.operatorId ? String(apt.operatorId) : undefined,
            alfadocs_practice_id: apt._practiceId ?? undefined,
            alfadocs_practice_name: apt._practiceName ?? undefined,
            alfadocs_config_id: apt._configId,
            source: 'alfadocs',
          },
        };
      });

      set({ alfadocsEvents: alfaEvents, alfadocsLoading: false });
    } catch (error) {
      console.warn('[Calendar] AlfaDocs appointments error:', error);
      set({ alfadocsEvents: [], alfadocsLoading: false });
    }
  },

  // Fetch Sales Activities as calendar events
  fetchSalesActivities: async (startDate, endDate) => {
    set({ salesActivitiesLoading: true, salesActivitiesError: null });

    try {
      // Get cached auth data to avoid duplicate API calls
      const authData = await getCachedAuthData();
      if (!authData) throw new Error('User not authenticated');

      // Calendari per persona: il calendario di X = task assegnate a X, o create da X senza assegnatario.
      // Si caricano solo i calendari che vedo (condivisioni e permessi);
      // cosa mostrare (spunte, livello, colori) lo decide getEnabledEvents. Senza condivisioni: come prima.
      const sharing = calendarSharingStore.getState();
      const ownerIds = sharing.ready && sharing.ctx?.meId === authData.userId
        ? Object.keys(sharing.visible).filter(id => /^[0-9a-f-]{36}$/i.test(id))
        : [authData.userId];
      if (!ownerIds.includes(authData.userId)) ownerIds.push(authData.userId);
      const inList = ownerIds.join(',');

      // A pagine da 1000: con molti calendari visibili (azienda grande, chi vede tutti) le righe superano il limite dell'API
      const page = (from: number) => supabase
        .from('sales_activities')
        .select(`
          *,
          deals!sales_activities_deal_id_fkey(
            id,
            title,
            client:clients(business_name)
          ),
          leads:clients!sales_activities_lead_id_fkey(
            id,
            business_name,
            stage:lead_pipeline_stages!clients_pipeline_stage_id_fkey(name, color)
          ),
          partners:external_partners!sales_activities_partner_id_fkey(
            id,
            name
          ),
          users:user_profiles!sales_activities_user_id_fkey1(
            full_name,
            email
          )
        `)
        .eq('organization_id', authData.organizationId)
        // Anche quelle create da me per chiunque (colleghi senza condivisione o disattivati), come prima
        .or(`assigned_to.in.(${inList}),and(assigned_to.is.null,user_id.in.(${inList})),user_id.eq.${authData.userId}`)
        .gte('due_date', startDate.toISOString())
        .lte('due_date', endDate.toISOString())
        .order('due_date', { ascending: true })
        .order('id', { ascending: true })
        .range(from, from + 999);
      // Si va avanti finche' arrivano righe (il limite dell'API potrebbe essere sotto 1000) e si scartano i doppioni
      const data: any[] = [];
      const seen = new Set<string>();
      for (let from = 0, i = 0; i < 20; i++) {
        const res = await page(from);
        if (res.error) throw res.error;
        const rows = res.data || [];
        if (!rows.length) break;
        for (const r of rows) if (!seen.has(r.id)) { seen.add(r.id); data.push(r); }
        from += rows.length;
      }

      // Convert sales activities to calendar events
      const salesActivityEvents: CalendarEvent[] = (data || []).map(activity => {
        const clientName = activity.deals?.client?.business_name
          || activity.leads?.business_name
          || (activity.partners?.name ? `Partner: ${activity.partners.name}` : 'Cliente sconosciuto');
        const dealTitle = activity.deals?.title ? ` - ${activity.deals.title}` : '';
        // La colonna is_completed NON esiste su sales_activities: chiusa =
        // completed_at valorizzato o status completed/cancelled (i due campi
        // possono divergere sulle righe storiche)
        const isCompleted = !!activity.completed_at || activity.status === 'completed' || activity.status === 'cancelled';

        return {
          id: `sales_activity_${activity.id}`,
          title: `${activity.title}${dealTitle}`,
          description: `${activity.description || ''}\n\nCliente: ${clientName}\nTipo: ${getActivityTypeLabel(activity.type ?? activity.activity_type)}\nCreato da: ${activity.users?.full_name || activity.users?.email || 'Non indicato'}`,
          start_time: activity.due_date,
          end_time: activity.end_date
            ? new Date(activity.end_date).toISOString()
            : new Date(new Date(activity.due_date).getTime() + 60 * 60 * 1000).toISOString(), // Use end_date if available, otherwise +1 hour
          all_day: false,
          source: 'sales_activities' as const,
          color: isCompleted ? '#10B981' : '#3B82F6',
          original_id: activity.id,
          event_type: 'sales_activity',
          metadata: {
            activity_type: activity.type ?? activity.activity_type,
            is_completed: isCompleted,
            deal_id: activity.deal_id,
            lead_id: activity.lead_id,
            assigned_to: activity.user_id,
            // Calendario di appartenenza (assegnatario, o chi l'ha creata) e chi l'ha fissata
            owner_id: activity.assigned_to || activity.user_id,
            creator_id: activity.user_id,
            outcome: activity.outcome || undefined,
            // Fase attuale del lead (dettaglio dell'appuntamento: si vede l'esito anche se e' stata cambiata la fase a mano)
            lead_stage_name: activity.leads?.stage?.name || undefined,
            lead_stage_color: activity.leads?.stage?.color || undefined,
            // Necessari al dedup dei rami Google/Microsoft in getEnabledEvents
            google_event_id: activity.google_event_id || undefined,
            microsoft_event_id: activity.microsoft_event_id || undefined
          }
        };
      });

      set({ 
        salesActivities: salesActivityEvents,
        salesActivitiesLoading: false 
      });

    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({ 
        salesActivitiesError: parsedError.message,
        salesActivitiesLoading: false 
      });
    }
  },

  // Fetch Project Tasks as calendar events
  fetchProjectTasks: async (startDate, endDate) => {
    set({ projectTasksLoading: true, projectTasksError: null });

    try {
      // Get cached auth data to avoid duplicate API calls
      const authData = await getCachedAuthData();
      if (!authData) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          project:projects!inner(
            id,
            name,
            organization_id,
            client:clients(business_name)
          ),
          assigned_user:user_profiles!tasks_assigned_to_fkey(
            full_name,
            email
          )
        `)
        .eq('project.organization_id', authData.organizationId)
        .gte('due_date', startDate.toISOString())
        .lte('due_date', endDate.toISOString())
        .order('due_date', { ascending: true });

      if (error) throw error;

      // Convert project tasks to calendar events
      const projectTaskEvents: CalendarEvent[] = (data || []).map(task => {
        const projectInfo = task.project ? ` - ${task.project.name}` : '';
        const clientName = task.project?.client?.business_name || '';
        const clientInfo = clientName ? `\nCliente: ${clientName}` : '';
        
        return {
          id: `project_task_${task.id}`,
          title: `${task.title}${projectInfo}`,
          description: `${task.description || ''}${clientInfo}\nPriorità: ${getPriorityLabel(task.priority)}\nAssegnato a: ${task.assigned_user?.full_name || task.assigned_user?.email || 'Non assegnato'}`,
          start_time: task.due_date,
          end_time: new Date(new Date(task.due_date).getTime() + 30 * 60 * 1000).toISOString(), // +30 minutes
          all_day: false,
          source: 'project_tasks' as const,
          color: getTaskStatusColor(task.status),
          original_id: task.id,
          event_type: 'project_task',
          metadata: {
            status: task.status,
            priority: task.priority,
            project_id: task.project_id,
            assigned_to: task.assigned_to
          }
        };
      });

      set({ 
        projectTasks: projectTaskEvents,
        projectTasksLoading: false 
      });

    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        projectTasksError: parsedError.message,
        projectTasksLoading: false
      });
    }
  },

  // WORK SLOTS METHODS WITH GOOGLE CALENDAR SYNC

  /**
   * Fetch work slots for a date range
   */
  fetchWorkSlots: async (startDate, endDate, assignedTo) => {
    // Store static properties to track last fetch time and range
    const now = Date.now();
    const lastFetchTime = (useCalendarStore as any).lastWorkSlotsFetchTime || 0;
    const lastStartDate = (useCalendarStore as any).lastWorkSlotsStartDate;
    const lastEndDate = (useCalendarStore as any).lastWorkSlotsEndDate;
    const lastAssignedTo = (useCalendarStore as any).lastWorkSlotsAssignedTo;

    // Minimum time between fetches (ms)
    const FETCH_COOLDOWN = 3000; // 3 seconds

    // Check if we've fetched this range recently (within cooldown period)
    const isSameRange = lastStartDate && lastEndDate &&
                        Math.abs(startDate.getTime() - lastStartDate.getTime()) < 1000 &&
                        Math.abs(endDate.getTime() - lastEndDate.getTime()) < 1000 &&
                        lastAssignedTo === assignedTo;

    if (isSameRange && now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('fetchWorkSlots: skipping duplicate fetch within cooldown period');
      return;
    }

    // Update tracking variables
    (useCalendarStore as any).lastWorkSlotsFetchTime = now;
    (useCalendarStore as any).lastWorkSlotsStartDate = startDate;
    (useCalendarStore as any).lastWorkSlotsEndDate = endDate;
    (useCalendarStore as any).lastWorkSlotsAssignedTo = assignedTo;

    // Guard against multiple simultaneous fetches
    const state = get();
    if (state.workSlotsLoading) {
      console.log('fetchWorkSlots: already loading, skipping');
      return;
    }

    set({ workSlotsLoading: true, workSlotsError: null });

    try {
      // Get valid session (with automatic refresh if needed)
      console.log('fetchWorkSlots: Getting valid session...');
      const session = await getValidSession();
      console.log('fetchWorkSlots: Session valid, access token present:', !!session.access_token);

      // Call Edge Function to fetch work slots with explicit Authorization header
      const { data, error } = await supabase.functions.invoke('get-work-slots', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          assigned_to: assignedTo || undefined,
        },
      });

      if (error) throw error;

      const workSlots: WorkSlot[] = data.work_slots || [];

      set({
        workSlots,
        workSlotsLoading: false
      });

    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        workSlotsError: parsedError.message,
        workSlotsLoading: false
      });
    }
  },

  /**
   * Create a work slot and sync to Google Calendar
   */
  createWorkSlot: async (data) => {
    set({ workSlotsLoading: true, workSlotsError: null });

    try {
      // Get valid session (with automatic refresh if needed)
      console.log('createWorkSlot: Getting valid session...');
      const session = await getValidSession();

      // Call Edge Function to create work slot (handles Google Calendar sync)
      const { data: responseData, error } = await supabase.functions.invoke('create-work-slot', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          project_id: data.projectId,
          project_name: data.projectName,
          start_time: data.startDate.toISOString(),
          end_time: data.endDate.toISOString(),
          assigned_to: data.assignedTo,
          task_ids: data.taskIds || [],
          metadata: data.metadata || {},
          google_calendar_id: data.googleCalendarId,
          description: data.description,
        },
      });

      if (error) throw error;

      const workSlot: WorkSlot = responseData.work_slot;

      // Add to local state
      set({
        workSlots: [...get().workSlots, workSlot],
        workSlotsLoading: false
      });

      return workSlot;
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        workSlotsError: parsedError.message,
        workSlotsLoading: false
      });
      return null;
    }
  },

  /**
   * Update a work slot and sync to Google Calendar
   */
  updateWorkSlot: async (id, data) => {
    set({ workSlotsLoading: true, workSlotsError: null });

    try {
      // Get valid session (with automatic refresh if needed)
      console.log('updateWorkSlot: Getting valid session...');
      const session = await getValidSession();

      // Call Edge Function to update work slot (handles Google Calendar sync)
      const { data: responseData, error } = await supabase.functions.invoke('update-work-slot', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          id,
          project_id: data.projectId,
          project_name: data.projectName,
          start_time: data.startDate?.toISOString(),
          end_time: data.endDate?.toISOString(),
          assigned_to: data.assignedTo,
          task_ids: data.taskIds,
          metadata: data.metadata,
        },
      });

      if (error) throw error;

      const updatedSlot: WorkSlot = responseData.work_slot;

      // Update local state
      set({
        workSlots: get().workSlots.map(slot => slot.id === id ? updatedSlot : slot),
        workSlotsLoading: false
      });

      return updatedSlot;
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        workSlotsError: parsedError.message,
        workSlotsLoading: false
      });
      return null;
    }
  },

  /**
   * Delete a work slot and remove from Google Calendar
   */
  deleteWorkSlot: async (id) => {
    set({ workSlotsLoading: true, workSlotsError: null });

    try {
      // Get valid session (with automatic refresh if needed)
      console.log('deleteWorkSlot: Getting valid session...');
      const session = await getValidSession();

      // Call Edge Function to delete work slot (handles Google Calendar sync)
      const { data: responseData, error } = await supabase.functions.invoke('delete-work-slot', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { id },
      });

      if (error) throw error;

      // Remove from local state
      set({
        workSlots: get().workSlots.filter(slot => slot.id !== id),
        workSlotsLoading: false
      });

      return true;
    } catch (error) {
      const parsedError = parseErrorResponse(error);
      set({
        workSlotsError: parsedError.message,
        workSlotsLoading: false
      });
      return false;
    }
  },

  // Reset store completely (for logout)
  resetStore: () => {
    // Clear localStorage cache
    clearCalendarListCache();
    localStorage.removeItem('google_calendar_sync_token');
    // Clear delta Microsoft dell'utente corrente (se noto)
    clearMicrosoftDelta(authCache?.userId);

    // Clear in-memory auth cache
    authCache = null;

    // Reset Zustand state
    set({
      events: [],
      googleEvents: [],
      microsoftEvents: [],
      salesActivities: [],
      projectTasks: [],
      bookingEvents: [],
      workSlots: [],
      calendarSources: [
        {
          id: 'crm',
          name: 'CRM Calendar',
          color: '#000000',
          enabled: true,
          type: 'crm'
        },
        {
          id: 'sales_activities',
          name: 'Sales Activities',
          color: '#3B82F6',
          enabled: true,
          type: 'sales_activities'
        },
        {
          id: 'project_tasks',
          name: 'Project Tasks',
          color: '#8B5CF6',
          enabled: true,
          type: 'project_tasks'
        },
        {
          id: 'bookings',
          name: 'Prenotazioni',
          color: '#F59E0B',
          enabled: true,
          type: 'bookings'
        },
        {
          id: 'alfadocs',
          name: 'AlfaDocs',
          color: '#2383E2',
          enabled: true,
          type: 'alfadocs'
        }
      ],
      loading: false,
      googleLoading: false,
      microsoftLoading: false,
      salesActivitiesLoading: false,
      projectTasksLoading: false,
      bookingEventsLoading: false,
      workSlotsLoading: false,
      error: null,
      googleError: null,
      microsoftError: null,
      salesActivitiesError: null,
      projectTasksError: null,
      bookingEventsError: null,
      workSlotsError: null,
      _syncToken: null
    });
    console.log('[CalendarStore] Store reset completed');
  }
}));

// Helper functions for activity and task labels
function getActivityTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    phone_call: 'Chiamata',
    call: 'Chiamata',
    videochiamata: 'Videochiamata',
    appuntamento: 'Appuntamento fisico',
    sopralluogo: 'Sopralluogo',
    email: 'Email',
    task: 'Attività',
    follow_up: 'Follow-up'
  };
  return labels[type] || type;
}

function getPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    low: 'Bassa',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente'
  };
  return labels[priority] || priority;
}

function getTaskStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: '#F59E0B',
    in_progress: '#3B82F6',
    completed: '#10B981',
    cancelled: '#6B7280',
    on_hold: '#EF4444'
  };
  return colors[status] || '#6B7280';
}

// Hook for using the calendar store in components
export const useCalendarStore = calendarStore;